import React, { useEffect, useId, useMemo, useState } from "react";
import useRole from "../../hooks/useRole";
import { getHRSettingsMasterData } from "../../services/employeeService";
import {
  addHRSettingRecord,
  editHRSettingRecord,
} from "../../services/HRSettingsService";


import "../../styles/hrPageShell.css";
const GROUPS = [
  {
    key: "organization",
    label: "Organization Setup",
    description: "Foundation setup for company structure before employee and policy assignment.",
  },
  {
    key: "security",
    label: "Roles & Scope",
    description: "Role scope and permission setup for Admin, HRAdmin and Manager operations.",
  },
  {
    key: "operations",
    label: "Operational Scheduling",
    description: "Operational master data for work units, shifts and weekly off patterns.",
  },
  {
    key: "policies",
    label: "HR Policies",
    description: "Attendance, leave and holiday policy setup used by EmployeeMaster, Attendance and LeaveManagement.",
  },
  {
    key: "assignment",
    label: "Employee Assignment Governance",
    description: "Employee policy assignment bridge and future assignment governance controls.",
  },
  {
    key: "quality",
    label: "Audit & Data Quality",
    description: "Configuration checks, duplicates, accidental imports and setup risks.",
  },
];

const ROLE_PERMISSIONS = {
  Admin: {
    canViewAll: true,
    editableGroups: ["organization", "security", "operations", "policies", "assignment", "quality"],
    canAdd: true,
    canEdit: true,
  },
  HRAdmin: {
    canViewAll: true,
    editableGroups: ["organization", "security", "operations", "policies", "assignment", "quality"],
    canAdd: true,
    canEdit: true,
  },
  Manager: {
    canViewAll: true,
    editableGroups: [],
    canAdd: false,
    canEdit: false,
  },
};

const ENTITY_CONFIG = [
  {
    key: "countries",
    group: "organization",
    label: "Countries",
    listName: "HR_Countries",
    ownerRole: "Admin",
    accessPolicy: "Admin can add/edit. HRAdmin can view. Manager has no setup access.",
    primaryField: "CountryCode",
    displayFields: ["CountryCode", "CountryName", "DefaultCurrency", "DefaultTimeZone", "IsActive"],
  },
  {
    key: "legalEntities",
    group: "organization",
    label: "Legal Entities",
    listName: "HR_LegalEntities",
    ownerRole: "Admin",
    accessPolicy: "Admin can add/edit. HRAdmin can view for employee/policy assignment context.",
    primaryField: "LegalEntityCode",
    displayFields: ["LegalEntityCode", "LegalEntityName", "CountryCode", "PayrollCountry", "DefaultCurrency", "EffectiveFrom", "EffectiveTo", "IsActive"],
  },
  {
    key: "locations",
    group: "organization",
    label: "Locations",
    listName: "HR_Locations",
    ownerRole: "Admin",
    accessPolicy: "Admin and HRAdmin can add/edit Locations. Manager can view only.",
    primaryField: "LocationCode",
    displayFields: ["LocationCode", "LocationName", "CountryCode", "LegalEntityCode", "RegionState", "City", "TimeZone", "IsRemoteAllowed", "IsActive"],
  },
  {
    key: "departments",
    group: "organization",
    label: "Departments",
    listName: "HR_Departments",
    ownerRole: "Admin",
    accessPolicy: "Admin can add/edit. HRAdmin can view and use for employee assignment.",
    primaryField: "DepartmentCode",
    displayFields: ["DepartmentCode", "DepartmentName", "ParentDepartmentCode", "DepartmentHeadEmployeeCode", "LegalEntityCode", "IsActive"],
  },
  {
    key: "workUnits",
    group: "organization",
    label: "Work Units",
    listName: "HR_WorkUnits",
    ownerRole: "Admin / HRAdmin",
    accessPolicy: "Admin/HRAdmin govern canonical Work Units. Manager access is view-only.",
    primaryField: "WorkUnitCode",
    displayFields: ["WorkUnitCode", "WorkUnitName", "DepartmentCode", "LocationCode", "CountryCode", "IndustryType", "WorkUnitType", "ManagerEmployeeCode", "OperationalRoles", "IsActive"],
  },
  {
    key: "roleScopeAssignments",
    group: "security",
    label: "Role Scope Assignments",
    listName: "HR_RoleScopeAssignments",
    ownerRole: "Admin / HRAdmin",
    accessPolicy: "Admin can fully manage. HRAdmin can manage HR/Manager scopes where permitted. Manager can view own scope only later.",
    primaryField: "UserEmail",
    displayFields: ["UserEmail", "EmployeeCode", "Role", "ScopeType", "ScopeValue", "CanView", "CanCreate", "CanEdit", "CanApprove", "CanAssignShift", "CanAssignWeeklyOff", "CanAssignWorkUnit", "EffectiveFrom", "EffectiveTo", "IsActive"],
  },
  {
    key: "workUnits",
    group: "operations",
    label: "Work Units",
    listName: "HR_WorkUnits",
    ownerRole: "Admin / HRAdmin",
    accessPolicy: "Canonical Work Unit scheduling projection. Manager access is view-only.",
    primaryField: "WorkUnitCode",
    displayFields: ["WorkUnitCode", "WorkUnitName", "DepartmentCode", "LocationCode", "CountryCode", "IndustryType", "WorkUnitType", "ManagerEmployeeCode", "OperationalRoles", "IsActive"],
  },
  {
    key: "shiftPatterns",
    group: "operations",
    label: "Shift Patterns",
    listName: "HR_ShiftPatterns",
    ownerRole: "HRAdmin / Admin / Manager scoped",
    accessPolicy: "Admin/HRAdmin can manage. Manager can assign/edit scoped shift patterns only when CanAssignShift is true.",
    primaryField: "ShiftPatternId",
    displayFields: ["ShiftPatternId", "ShiftName", "CountryCode", "LocationCode", "IndustryType", "EmploymentType", "WorkUnitCode", "ShiftType", "StartTime", "EndTime", "BreakMinutes", "IsRotational", "IsNightShift", "IsHourly", "IsActive"],
  },
  {
    key: "weeklyOffPatterns",
    group: "operations",
    label: "Weekly Off Patterns",
    listName: "HR_WeeklyOffPatterns",
    ownerRole: "HRAdmin / Admin / Manager scoped",
    accessPolicy: "Admin/HRAdmin can manage. Manager can assign/edit scoped weekly offs only when CanAssignWeeklyOff is true.",
    primaryField: "WeeklyOffPatternId",
    displayFields: ["WeeklyOffPatternId", "PatternName", "CountryCode", "LocationCode", "IndustryType", "EmploymentType", "WorkUnitCode", "WeeklyOffType", "FixedDays", "IsRotational", "IsRosterDriven", "IsActive"],
  },
  {
    key: "employmentTypes",
    group: "policies",
    label: "Employment Types",
    listName: "HR_EmploymentTypes",
    ownerRole: "Admin / HRAdmin",
    accessPolicy: "Admin/HRAdmin can manage. Manager view only.",
    primaryField: "EmploymentTypeCode",
    displayFields: ["EmploymentTypeCode", "EmploymentTypeName", "CountryCode", "DefaultLeaveEligibilityMode", "DefaultFTEPercent", "IsPayrollEmployee", "IsShiftEligible", "IsActive"],
  },
  {
    key: "attendancePolicies",
    group: "policies",
    label: "Attendance Policies",
    listName: "HR_AttendancePolicies",
    ownerRole: "HRAdmin / Admin",
    accessPolicy: "Admin/HRAdmin can add/edit. Manager view only.",
    primaryField: "AttendancePolicyId",
    displayFields: ["AttendancePolicyId", "PolicyName", "CountryCode", "LegalEntityCode", "LocationCode", "IndustryType", "EmploymentType", "GraceMinutes", "MinimumHoursPerDay", "OvertimeApplicable", "LateMarkRule", "Status", "IsDefault", "EffectiveFrom", "EffectiveTo", "IsActive"],
  },
  {
    key: "leavePolicies",
    group: "policies",
    label: "Leave Policies",
    listName: "HR_LeavePolicies",
    ownerRole: "HRAdmin / Admin",
    accessPolicy: "Admin/HRAdmin can add/edit. Manager view only.",
    primaryField: "LeavePolicyId",
    displayFields: ["LeavePolicyId", "PolicyName", "CountryCode", "LegalEntityCode", "LocationCode", "IndustryType", "EmploymentType", "EffectiveFrom", "EffectiveTo", "Status", "IsDefault", "AllowOverride", "RequiresApproval", "OwnerRole", "IsActive"],
  },
  {
    key: "leavePolicyRules",
    group: "policies",
    label: "Leave Policy Rules",
    listName: "HR_LeavePolicyRules",
    ownerRole: "HRAdmin / Admin",
    accessPolicy: "Admin/HRAdmin can add/edit entitlement rules. Manager view only.",
    primaryField: "LeavePolicyId",
    displayFields: ["LeavePolicyId", "LeaveType", "LeaveCategory", "EntitledDays", "AccrualMethod", "CarryForwardAllowed", "MaxCarryForwardDays", "MaxConsecutiveDays", "RequiresProof", "AllowHalfDay", "AllowNegativeBalance", "IsPaid", "IsActive"],
  },
  {
    key: "holidayCalendars",
    group: "policies",
    label: "Holiday Calendars",
    listName: "HR_HolidayCalendars",
    ownerRole: "HRAdmin / Admin",
    accessPolicy: "Admin/HRAdmin can add/edit calendar headers. Manager view only.",
    primaryField: "HolidayCalendarId",
    displayFields: ["HolidayCalendarId", "CalendarName", "CountryCode", "LegalEntityCode", "LocationCode", "Year", "IsDefault", "IsActive"],
  },
  {
    key: "holidayCalendarDates",
    group: "policies",
    label: "Holiday Dates",
    listName: "HR_HolidayCalendarDates",
    ownerRole: "HRAdmin / Admin",
    accessPolicy: "Admin/HRAdmin can add/edit holiday dates. Manager view only.",
    primaryField: "HolidayCalendarId",
    displayFields: ["HolidayCalendarId", "HolidayDate", "HolidayName", "HolidayType", "IsOptional", "IsPaidHoliday", "AppliesToWorkUnitCode", "IsActive"],
  },
  {
    key: "employeePolicyAssignments",
    group: "assignment",
    label: "Employee Policy Assignments",
    listName: "HR_EmployeePolicyAssignments",
    ownerRole: "HRAdmin / Admin",
    accessPolicy: "Governance viewer only. EmployeeMaster save flow is the source of truth for creating and changing assignments.",
    primaryField: "EmployeeCode",
    displayFields: ["EmployeeCode", "CountryCode", "IsCurrent", "LeavePolicyId", "AttendancePolicyId", "ShiftPatternId", "WeeklyOffPatternId", "HolidayCalendarId", "LegalEntityCode", "LocationCode", "DepartmentCode", "WorkUnitCode", "EffectiveFrom", "EffectiveTo", "AssignedBy", "AssignmentReason", "IsActive"],
    emptyMessage: "No employee policy assignments loaded yet. Get_HRMasterData should return employeePolicyAssignments from HR_EmployeePolicyAssignments. Assignment create/change is owned by EmployeeMaster save flow.",
  },
  {
    key: "leaveBalances",
    group: "assignment",
    label: "Leave Balances",
    listName: "HR_LeaveBalances",
    ownerRole: "HRAdmin / Admin",
    accessPolicy: "Governance read model. Mutations remain controlled by Leave Management processing and authorized adjustment workflows.",
    primaryField: "EmployeeCode",
    displayFields: ["EmployeeCode", "LeavePolicyId", "LeaveType", "BalanceYear", "OpeningBalance", "AccruedAmount", "UsedAmount", "PendingAmount", "AdjustedAmount", "AvailableBalance", "AsOfDate", "CalculationVersion", "IsActive"],
    emptyMessage: "No leave balances loaded yet. Track B should return leaveBalances from hr.leave_balance.",
  },
];

// =====================================================
// Phase 3F.17F.3 - Editor fields
// Countries is activated first because the Countries action flow is ready for testing.
// Add more entity keys here after each entity action branch is completed in Power Automate.
// =====================================================
// =====================================================
// Phase 3F.17F.4 - Smart dependency-aware editor fields
// Add/Edit only. Deactivate/reactivate is handled through IsActive in Edit form.
// Dependent fields use dropdowns from currently loaded master data.
// Primary IDs are generated/suggested where standards are clear.
// =====================================================
const EDITOR_FIELDS = {
  countries: [
    { name: "CountryCode", label: "Country Code (ISO Alpha-2)", required: true, codeInput: true },
    { name: "CountryName", label: "Country Name", required: true, hint: "Governed matches derive technical standards fields." },
    { name: "ISOAlpha3Code", label: "ISO Alpha-3 Code", derived: true },
    { name: "ISONumericCode", label: "ISO Numeric Code", derived: true },
    { name: "ShortName", label: "Short Name", derived: true },
    { name: "RegionGroup", label: "Region Group", derived: true, options: ["APAC", "EMEA", "AMER", "GLOBAL"] },
    { name: "DefaultCurrency", label: "Default Currency", source: { entityKey: "currencies", valueField: "code", labelFields: ["code", "name"] } },
    { name: "DefaultTimeZone", label: "Default Time Zone", source: { entityKey: "timeZones", valueField: "code", labelFields: ["code", "name"] } },
    { name: "DefaultLanguageCode", label: "Default Language", source: { entityKey: "languages", valueField: "code", labelFields: ["code", "name"] }, hint: "National default is suggested; English remains selectable." },
    { name: "PhoneCountryCode", label: "Phone Country Code", derived: true },
    { name: "DateFormatCode", label: "Date Format", derived: true },
    { name: "NumberFormatCode", label: "Number Format", derived: true },
    { name: "WeekStartDay", label: "Week Start Day", derived: true, options: ["1","2","3","4","5","6","7"] },
    { name: "EffectiveFrom", label: "Effective From", type: "date" },
    { name: "EffectiveTo", label: "Effective To", type: "date" },
    { name: "IsActive", label: "Active", type: "checkbox" },
    { name: "DisplayOrder", label: "Display Order", type: "number" },
  ],
  legalEntities: [
    { name: "CountryCode", label: "Country", required: true, source: { entityKey: "countries", valueField: "CountryCode", labelFields: ["CountryCode", "CountryName"] } },
    { name: "LegalEntityCode", label: "Legal Entity Code", required: true, generated: true, hint: "Generated from selected country" },
    { name: "LegalEntityName", label: "Legal Entity Name", required: true },
    { name: "PayrollCountry", label: "Payroll Country", source: { entityKey: "countries", valueField: "CountryCode", labelFields: ["CountryCode", "CountryName"] } },
    { name: "DefaultCurrency", label: "Default Currency", source: { entityKey: "currencies", valueField: "code", labelFields: ["code", "name"] } },
    { name: "IsActive", label: "Active", type: "checkbox" },
    { name: "EffectiveFrom", label: "Effective From", type: "date" },
    { name: "EffectiveTo", label: "Effective To", type: "date" },
  ],
  locations: [
    { name: "CountryCode", label: "Country", required: true, source: { entityKey: "countries", valueField: "CountryCode", labelFields: ["CountryCode", "CountryName"] } },
    { name: "LegalEntityCode", label: "Legal Entity", required: true, source: { entityKey: "legalEntities", valueField: "LegalEntityCode", labelFields: ["LegalEntityCode", "LegalEntityName"], filters: [{ field: "CountryCode", targetField: "CountryCode" }] } },
    { name: "LocationTypeCode", label: "Location Type", source: { entityKey: "locationTypes", valueField: "code", labelFields: ["code", "name"] } },
    { name: "City", label: "City", required: true },
    { name: "LocationCode", label: "Location Code", required: true, generated: true, hint: "Generated from country and city" },
    { name: "LocationName", label: "Location Name", required: true },
    { name: "SiteCode", label: "Site Code", codeInput: true, hint: "Optional facility, ERP, MES, warehouse, or customer operational code; it does not replace Location Code." },
    { name: "AddressLine1", label: "Address Line 1" },
    { name: "AddressLine2", label: "Address Line 2" },
    { name: "RegionState", label: "Region / State" },
    { name: "PostalCode", label: "Postal Code" },
    { name: "Latitude", label: "Latitude", type: "number", hint: "Optional advanced value. Suggested only from an exact governed City and Postal Code match." },
    { name: "Longitude", label: "Longitude", type: "number", hint: "Optional advanced value. Suggested only from an exact governed City and Postal Code match." },
    { name: "CoordinateProvenance", label: "Coordinate Provenance", uiOnly: true, options: ["Manual", "Imported", "Geocoded"], hint: "UI-only context; never sent to the API." },
    { name: "TimeZone", label: "Time Zone", source: { entityKey: "timeZones", valueField: "code", labelFields: ["code", "name"] } },
    { name: "DefaultLanguageCode", label: "Default Language", source: { entityKey: "languages", valueField: "code", labelFields: ["code", "name"] } },
    { name: "DefaultCalendarCode", label: "Default Calendar Code", codeInput: true },
    { name: "IsRemoteAllowed", label: "Remote Allowed", type: "checkbox" },
    { name: "IsActive", label: "Active", type: "checkbox" },
    { name: "EffectiveFrom", label: "Effective From", type: "date" },
    { name: "EffectiveTo", label: "Effective To", type: "date" },
  ],
  departments: [
    { name: "CountryCode", label: "Country", required: true, uiOnly: true, source: { entityKey: "countries", valueField: "CountryCode", labelFields: ["CountryCode", "CountryName"] }, hint: "Filtering context only; Country is not persisted on Department." },
    { name: "LegalEntityCode", label: "Legal Entity", required: true, source: { entityKey: "legalEntities", valueField: "LegalEntityCode", labelFields: ["LegalEntityCode", "LegalEntityName"], filters: [{ field: "CountryCode", targetField: "CountryCode" }] } },
    { name: "DepartmentName", label: "Department Name", required: true },
    { name: "DepartmentCode", label: "Department Code", required: true, generated: true, hint: "Generated from department name" },
    { name: "ParentDepartmentCode", label: "Parent Department", emptyHint: "No compatible parent Department exists yet. Create the first Department without a parent.", source: { entityKey: "departments", valueField: "DepartmentCode", labelFields: ["DepartmentCode", "DepartmentName"], filters: [{ field: "LegalEntityCode", targetField: "LegalEntityCode" }] } },
    { name: "DepartmentHeadEmployeeCode", label: "Department Head Employee Code", codeInput: true },
    { name: "DepartmentTypeCode", label: "Department Type Code", codeInput: true },
    { name: "CostCenterCode", label: "Cost Center Code", codeInput: true },
    { name: "IsActive", label: "Active", type: "checkbox" },
    { name: "EffectiveFrom", label: "Effective From", type: "date" },
    { name: "EffectiveTo", label: "Effective To", type: "date" },
    { name: "DisplayOrder", label: "Display Order", type: "number" },
  ],
  workUnits: [
    { name: "LegalEntityCode", label: "Legal Entity", required: true, source: { entityKey: "legalEntities", valueField: "LegalEntityCode", labelFields: ["LegalEntityCode", "LegalEntityName"] } },
    { name: "CountryCode", label: "Country", required: true, source: { entityKey: "countries", valueField: "CountryCode", labelFields: ["CountryCode", "CountryName"] } },
    { name: "LocationCode", label: "Location", required: true, source: { entityKey: "locations", valueField: "LocationCode", labelFields: ["LocationCode", "LocationName"], filters: [{ field: "CountryCode", targetField: "CountryCode" }, { field: "LegalEntityCode", targetField: "LegalEntityCode" }] } },
    { name: "DepartmentCode", label: "Department", required: true, source: { entityKey: "departments", valueField: "DepartmentCode", labelFields: ["DepartmentCode", "DepartmentName"], filters: [{ field: "LegalEntityCode", targetField: "LegalEntityCode" }] } },
    { name: "WorkUnitType", label: "Work Unit Type", required: true, source: { entityKey: "workUnitTypes", valueField: "code", labelFields: ["code", "name"] } },
    { name: "IndustryType", label: "Operational Industry Context", required: true, source: { entityKey: "industryTypes", valueField: "code", labelFields: ["code", "name"] }, hint: "Identifies the operating context of this Work Unit, such as Manufacturing, Processing, Logistics, Services, Retail, or Corporate. Use the context that governs scheduling, policies, compliance, analytics, and MES behavior for this Work Unit." },
    { name: "WorkUnitCode", label: "Work Unit Code", required: true, generated: true, hint: "Generated from country, location, and work unit type" },
    { name: "WorkUnitName", label: "Work Unit Name", required: true },
    { name: "ParentWorkUnitCode", label: "Parent Work Unit", emptyHint: "No compatible parent Work Unit exists yet. Create the first Work Unit without a parent.", source: { entityKey: "workUnits", valueField: "WorkUnitCode", labelFields: ["WorkUnitCode", "WorkUnitName"], filters: [{ field: "LegalEntityCode", targetField: "LegalEntityCode" }, { field: "DepartmentCode", targetField: "DepartmentCode" }] } },
    { name: "ManagerEmployeeCode", label: "Manager Employee Code", codeInput: true },
    { name: "OwnerRole", label: "Owner Role", options: ["ADMIN", "HRADMIN", "MANAGER"], hint: "Accountable governance role for this canonical Work Unit; this is not an employee assignment." },
    { name: "OperationalRoles", label: "Operational Roles", type: "multiselect", options: ["OPERATOR", "SUPERVISOR", "QUALITY", "MAINTENANCE", "WAREHOUSE", "PLANNER"], hint: "Structured roles permitted to perform or supervise operations; these are not employee assignments." },
    { name: "OperatingCalendarCode", label: "Operating Calendar Code", codeInput: true },
    { name: "DefaultTimeZone", label: "Default Time Zone", source: { entityKey: "timeZones", valueField: "code", labelFields: ["code", "name"] } },
    { name: "CostCenterCode", label: "Cost Center Code", codeInput: true },
    { name: "IsMESEnabled", label: "MES Enabled", type: "checkbox", hint: "Marks whether this canonical Work Unit participates in MES execution or integration." },
    { name: "MESWorkCenterReference", label: "MES Work Center Reference", dependsOn: { field: "IsMESEnabled", equals: true }, hint: "Optional external MES or ERP work-centre identifier; available only when MES Enabled is selected." },
    { name: "IsActive", label: "Active", type: "checkbox" },
    { name: "EffectiveFrom", label: "Effective From", type: "date" },
    { name: "EffectiveTo", label: "Effective To", type: "date" },
    { name: "DisplayOrder", label: "Display Order", type: "number" },
  ],
  roleScopeAssignments: [
    { name: "UserEmail", label: "User Email", required: true, type: "email" },
    { name: "EmployeeCode", label: "Employee Code" },
    { name: "Role", label: "Role", required: true, options: ["Admin", "HRAdmin", "Manager"] },
    { name: "ScopeType", label: "Scope Type", required: true, options: ["Country", "LegalEntity", "Location", "Department", "WorkUnit"] },
    { name: "ScopeValue", label: "Scope Value", required: true, dynamicSource: "scopeValue", hint: "Options change based on Scope Type" },
    { name: "CanView", label: "Can View", type: "checkbox" },
    { name: "CanCreate", label: "Can Create", type: "checkbox" },
    { name: "CanEdit", label: "Can Edit", type: "checkbox" },
    { name: "CanApprove", label: "Can Approve", type: "checkbox" },
    { name: "CanAssignShift", label: "Can Assign Shift", type: "checkbox" },
    { name: "CanAssignWeeklyOff", label: "Can Assign Weekly Off", type: "checkbox" },
    { name: "CanAssignWorkUnit", label: "Can Assign Work Unit", type: "checkbox" },
    { name: "EffectiveFrom", label: "Effective From", type: "date" },
    { name: "EffectiveTo", label: "Effective To", type: "date" },
    { name: "IsActive", label: "Active", type: "checkbox" },
  ],
  employmentTypes: [
    { name: "CountryCode", label: "Country", source: { entityKey: "countries", valueField: "CountryCode", labelFields: ["CountryCode", "CountryName"] }, hint: "Leave blank for global applicability." },
    { name: "EmploymentTypeName", label: "Employment Type Name", required: true, hint: "Governed contractual engagement category, such as Permanent, Fixed Term, Temporary, Apprentice, Intern, or Contractor." },
    { name: "EmploymentTypeCode", label: "Employment Type Code", required: true, generated: true, hint: "Generated from country and employment type" },
    { name: "DefaultLeaveEligibilityMode", label: "Default Leave Eligibility Mode", options: ["ELIGIBLE", "NOT_ELIGIBLE", "PRORATED", "CUSTOM"] },
    { name: "DefaultFTEPercent", label: "Default FTE Percent", type: "number" },
    { name: "IsPayrollEmployee", label: "Payroll Employee", type: "checkbox" },
    { name: "IsShiftEligible", label: "Shift Eligible", type: "checkbox" },
    { name: "WorkerCategoryCode", label: "Worker Category Code", codeInput: true, hint: "Workforce, statutory, or payroll classification distinct from Employment Type. Use only an approved governed code." },
    { name: "DefaultHoursPerWeek", label: "Default Hours Per Week", type: "number" },
    { name: "OvertimeEligible", label: "Overtime Eligible", type: "checkbox" },
    { name: "IsActive", label: "Active", type: "checkbox" },
    { name: "EffectiveFrom", label: "Effective From", type: "date" },
    { name: "EffectiveTo", label: "Effective To", type: "date" },
  ],
  attendancePolicies: [
    { name: "CountryCode", label: "Country", required: true, source: { entityKey: "countries", valueField: "CountryCode", labelFields: ["CountryCode", "CountryName"] } },
    { name: "LegalEntityCode", label: "Legal Entity", source: { entityKey: "legalEntities", valueField: "LegalEntityCode", labelFields: ["LegalEntityCode", "LegalEntityName"], filters: [{ field: "CountryCode", targetField: "CountryCode" }] } },
    { name: "LocationCode", label: "Location", source: { entityKey: "locations", valueField: "LocationCode", labelFields: ["LocationCode", "LocationName"], filters: [{ field: "CountryCode", targetField: "CountryCode" }] } },
    { name: "EmploymentType", label: "Employment Types", type: "multiselect", source: { entityKey: "employmentTypes", valueField: "EmploymentTypeCode", labelFields: ["EmploymentTypeCode", "EmploymentTypeName"], filters: [{ field: "CountryCode", targetField: "CountryCode" }] } },
    { name: "AttendancePolicyId", label: "Attendance Policy ID", required: true, generated: true, hint: "Generated from country and employment type" },
    { name: "PolicyName", label: "Policy Name", required: true },
    { name: "IndustryType", label: "Industry Type", type: "multiselect", source: { entityKey: "industryTypes", valueField: "code", labelFields: ["code", "name"] } },
    { name: "GraceMinutes", label: "Grace Minutes", type: "number" },
    { name: "MinimumHoursPerDay", label: "Minimum Hours Per Day", type: "number" },
    { name: "OvertimeApplicable", label: "Overtime Applicable", type: "checkbox" },
    { name: "LateMarkRule", label: "Late Mark Rule" },
    { name: "Status", label: "Status", options: ["Draft", "Active", "Retired"] },
    { name: "IsDefault", label: "Default", type: "checkbox" },
    { name: "EffectiveFrom", label: "Effective From", type: "date" },
    { name: "EffectiveTo", label: "Effective To", type: "date" },
    { name: "IsActive", label: "Active", type: "checkbox" },
  ],
  leavePolicies: [
    { name: "CountryCode", label: "Country", required: true, source: { entityKey: "countries", valueField: "CountryCode", labelFields: ["CountryCode", "CountryName"] } },
    { name: "LegalEntityCode", label: "Legal Entity", source: { entityKey: "legalEntities", valueField: "LegalEntityCode", labelFields: ["LegalEntityCode", "LegalEntityName"], filters: [{ field: "CountryCode", targetField: "CountryCode" }] } },
    { name: "LocationCode", label: "Location", source: { entityKey: "locations", valueField: "LocationCode", labelFields: ["LocationCode", "LocationName"], filters: [{ field: "CountryCode", targetField: "CountryCode" }] } },
    { name: "EmploymentType", label: "Employment Types", type: "multiselect", source: { entityKey: "employmentTypes", valueField: "EmploymentTypeCode", labelFields: ["EmploymentTypeCode", "EmploymentTypeName"], filters: [{ field: "CountryCode", targetField: "CountryCode" }] } },
    { name: "LeavePolicyId", label: "Leave Policy ID", required: true, generated: true, hint: "Generated from country and employment type" },
    { name: "PolicyName", label: "Policy Name", required: true },
    { name: "IndustryType", label: "Industry Type", type: "multiselect", source: { entityKey: "industryTypes", valueField: "code", labelFields: ["code", "name"] } },
    { name: "EffectiveFrom", label: "Effective From", type: "date" },
    { name: "EffectiveTo", label: "Effective To", type: "date" },
    { name: "Status", label: "Status", options: ["Draft", "Active", "Retired"] },
    { name: "IsDefault", label: "Default", type: "checkbox" },
    { name: "AllowOverride", label: "Allow Override", type: "checkbox" },
    { name: "RequiresApproval", label: "Requires Approval", type: "checkbox" },
    { name: "OwnerRole", label: "Owner Role", options: ["Admin", "HRAdmin", "Manager"] },
    { name: "IsActive", label: "Active", type: "checkbox" },
  ],
  leavePolicyRules: [
    { name: "CountryCode", label: "Country Filter", uiOnly: true, source: { entityKey: "countries", valueField: "CountryCode", labelFields: ["CountryCode", "CountryName"] }, hint: "Filter leave policies by country before selecting a policy" },
    { name: "LeavePolicyId", label: "Leave Policy", required: true, source: { entityKey: "leavePolicies", valueField: "LeavePolicyId", labelFields: ["LeavePolicyId", "PolicyName"], filters: [{ field: "CountryCode", targetField: "CountryCode" }] } },
    { name: "LeaveType", label: "Leave Type", required: true, options: ["CL", "SL", "EL", "PL", "AL", "LOP", "ML", "PLT"] },
    { name: "LeaveCategory", label: "Leave Category", options: ["Casual", "Sick", "Earned", "Privilege", "Annual", "Unpaid", "Maternity", "Paternity"] },
    { name: "EntitledDays", label: "Entitled Days", type: "number" },
    { name: "AccrualMethod", label: "Accrual Method", options: ["Annual", "Monthly", "Quarterly", "Manual", "None"] },
    { name: "CarryForwardAllowed", label: "Carry Forward Allowed", type: "checkbox" },
    { name: "MaxCarryForwardDays", label: "Max Carry Forward Days", type: "number" },
    { name: "MaxConsecutiveDays", label: "Max Consecutive Days", type: "number" },
    { name: "RequiresProof", label: "Requires Proof", type: "checkbox" },
    { name: "AllowHalfDay", label: "Allow Half Day", type: "checkbox" },
    { name: "AllowNegativeBalance", label: "Allow Negative Balance", type: "checkbox" },
    { name: "IsPaid", label: "Paid", type: "checkbox" },
    { name: "IsActive", label: "Active", type: "checkbox" },
  ],
  shiftPatterns: [
    { name: "CountryCode", label: "Country", source: { entityKey: "countries", valueField: "CountryCode", labelFields: ["CountryCode", "CountryName"] } },
    { name: "LocationCode", label: "Location", source: { entityKey: "locations", valueField: "LocationCode", labelFields: ["LocationCode", "LocationName"], filters: [{ field: "CountryCode", targetField: "CountryCode" }] } },
    { name: "WorkUnitCode", label: "Work Unit", source: { entityKey: "workUnits", valueField: "WorkUnitCode", labelFields: ["WorkUnitCode", "WorkUnitName"], filters: [{ field: "CountryCode", targetField: "CountryCode" }, { field: "LocationCode", targetField: "LocationCode" }] } },
    { name: "EmploymentType", label: "Employment Type", source: { entityKey: "employmentTypes", valueField: "EmploymentTypeCode", labelFields: ["EmploymentTypeCode", "EmploymentTypeName"], filters: [{ field: "CountryCode", targetField: "CountryCode" }] } },
    { name: "ShiftType", label: "Shift Type", required: true, options: ["Day", "Night", "General", "Rotational", "Hourly"] },
    { name: "ShiftPatternId", label: "Shift Pattern ID", required: true, generated: true, hint: "Generated from country, location, and shift type" },
    { name: "ShiftName", label: "Shift Name", required: true },
    { name: "IndustryType", label: "Industry Type", type: "multiselect", source: { entityKey: "industryTypes", valueField: "code", labelFields: ["code", "name"] } },
    { name: "StartTime", label: "Start Time", type: "time" },
    { name: "EndTime", label: "End Time", type: "time" },
    { name: "BreakMinutes", label: "Break Minutes", type: "number" },
    { name: "PaidMinutes", label: "Paid Minutes", type: "number", hint: "Paid minutes within the scheduled shift." },
    { name: "ScheduledMinutes", label: "Scheduled Minutes", type: "number", hint: "Total scheduled shift minutes before policy evaluation." },
    { name: "CrossesMidnight", label: "Crosses Midnight", type: "checkbox", hint: "Select when the shift ends on the following calendar day." },
    { name: "EarlyEntryToleranceMinutes", label: "Early Entry Tolerance Minutes", type: "number" },
    { name: "LateEntryToleranceMinutes", label: "Late Entry Tolerance Minutes", type: "number" },
    { name: "EarlyExitToleranceMinutes", label: "Early Exit Tolerance Minutes", type: "number" },
    { name: "LateExitToleranceMinutes", label: "Late Exit Tolerance Minutes", type: "number" },
    { name: "StartGraceMinutes", label: "Start Grace Minutes", type: "number" },
    { name: "EndGraceMinutes", label: "End Grace Minutes", type: "number" },
    { name: "IsRotational", label: "Rotational", type: "checkbox" },
    { name: "IsNightShift", label: "Night Shift", type: "checkbox" },
    { name: "IsHourly", label: "Hourly", type: "checkbox" },
    { name: "OwnerRole", label: "Owner Role", options: ["Admin", "HRAdmin", "Manager"] },
    { name: "OperationalRoles", label: "Operational Roles", type: "multiselect", options: ["OPERATOR", "SUPERVISOR", "QUALITY", "MAINTENANCE", "WAREHOUSE", "PLANNER"] },
    { name: "IsActive", label: "Active", type: "checkbox" },
    { name: "EffectiveFrom", label: "Effective From", type: "date" },
    { name: "EffectiveTo", label: "Effective To", type: "date" },
    { name: "DisplayOrder", label: "Display Order", type: "number" },
  ],
  weeklyOffPatterns: [
    { name: "CountryCode", label: "Country", source: { entityKey: "countries", valueField: "CountryCode", labelFields: ["CountryCode", "CountryName"] } },
    { name: "LocationCode", label: "Location", source: { entityKey: "locations", valueField: "LocationCode", labelFields: ["LocationCode", "LocationName"], filters: [{ field: "CountryCode", targetField: "CountryCode" }] } },
    { name: "WorkUnitCode", label: "Work Unit", source: { entityKey: "workUnits", valueField: "WorkUnitCode", labelFields: ["WorkUnitCode", "WorkUnitName"], filters: [{ field: "CountryCode", targetField: "CountryCode" }, { field: "LocationCode", targetField: "LocationCode" }] } },
    { name: "EmploymentType", label: "Employment Type", source: { entityKey: "employmentTypes", valueField: "EmploymentTypeCode", labelFields: ["EmploymentTypeCode", "EmploymentTypeName"], filters: [{ field: "CountryCode", targetField: "CountryCode" }] } },
    { name: "WeeklyOffType", label: "Weekly Off Type", required: true, options: ["Fixed", "Rotational", "RosterDriven"] },
    { name: "WeeklyOffPatternId", label: "Weekly Off Pattern ID", required: true, generated: true, hint: "Generated from country, location, and weekly off type" },
    { name: "PatternName", label: "Pattern Name", required: true },
    { name: "IndustryType", label: "Industry Type", type: "multiselect", source: { entityKey: "industryTypes", valueField: "code", labelFields: ["code", "name"] } },
    { name: "FixedDays", label: "Fixed Days", options: ["Sunday", "Saturday-Sunday", "Friday", "Friday-Saturday", "Custom"] },
    { name: "IsRotational", label: "Rotational", type: "checkbox" },
    { name: "RotationRule", label: "Rotation Rule" },
    { name: "RotationCycleDays", label: "Rotation Cycle Days", type: "number", hint: "Number of days in the complete rotation cycle." },
    { name: "RotationAnchorDate", label: "Rotation Anchor Date", type: "date", hint: "Date from which the rotation cycle is calculated." },
    { name: "PatternDays", label: "Pattern Days", type: "multiselect", options: ["1", "2", "3", "4", "5", "6", "7"], hint: "Structured cycle-day rows stored in Track B." },
    { name: "IsRosterDriven", label: "Roster Driven", type: "checkbox" },
    { name: "OwnerRole", label: "Owner Role", options: ["Admin", "HRAdmin", "Manager"] },
    { name: "OperationalRoles", label: "Operational Roles", type: "multiselect", options: ["OPERATOR", "SUPERVISOR", "QUALITY", "MAINTENANCE", "WAREHOUSE", "PLANNER"] },
    { name: "IsActive", label: "Active", type: "checkbox" },
    { name: "EffectiveFrom", label: "Effective From", type: "date" },
    { name: "EffectiveTo", label: "Effective To", type: "date" },
    { name: "DisplayOrder", label: "Display Order", type: "number" },
  ],
  holidayCalendars: [
    { name: "CountryCode", label: "Country", required: true, source: { entityKey: "countries", valueField: "CountryCode", labelFields: ["CountryCode", "CountryName"] } },
    { name: "LegalEntityCode", label: "Legal Entity", source: { entityKey: "legalEntities", valueField: "LegalEntityCode", labelFields: ["LegalEntityCode", "LegalEntityName"], filters: [{ field: "CountryCode", targetField: "CountryCode" }] } },
    { name: "LocationCode", label: "Location", source: { entityKey: "locations", valueField: "LocationCode", labelFields: ["LocationCode", "LocationName"], filters: [{ field: "CountryCode", targetField: "CountryCode" }] } },
    { name: "Year", label: "Year", type: "number", required: true },
    { name: "HolidayCalendarId", label: "Holiday Calendar ID", required: true, generated: true, hint: "Generated from country and year" },
    { name: "CalendarName", label: "Calendar Name", required: true },
    { name: "CalendarScopeType", label: "Calendar Scope Type", options: ["COUNTRY", "LEGAL_ENTITY", "LOCATION", "WORK_UNIT"], hint: "Track B scope classification for this calendar." },
    { name: "CalendarScopeReference", label: "Calendar Scope Reference", hint: "Required together with Calendar Scope Type." },
    { name: "EffectiveFrom", label: "Effective From", type: "date" },
    { name: "EffectiveTo", label: "Effective To", type: "date" },
    { name: "IsDefault", label: "Default", type: "checkbox" },
    { name: "IsActive", label: "Active", type: "checkbox" },
  ],
  holidayCalendarDates: [
    { name: "HolidayCalendarId", label: "Holiday Calendar", required: true, source: { entityKey: "holidayCalendars", valueField: "HolidayCalendarId", labelFields: ["HolidayCalendarId", "CalendarName"] } },
    { name: "HolidayDate", label: "Holiday Date", required: true, type: "date" },
    { name: "HolidayName", label: "Holiday Name", required: true },
    { name: "HolidayType", label: "Holiday Type", options: ["National", "Regional", "Company", "Optional", "Festival", "Other"] },
    { name: "IsOptional", label: "Optional", type: "checkbox" },
    { name: "IsPaidHoliday", label: "Paid Holiday", type: "checkbox" },
    { name: "StartTime", label: "Start Time", type: "time", hint: "Optional partial-day holiday start time; provide with End Time." },
    { name: "EndTime", label: "End Time", type: "time", hint: "Optional partial-day holiday end time; provide with Start Time." },
    { name: "AppliesToWorkUnitCodes", label: "Applies To Work Units", type: "multiselect", options: ["ALL"], source: { entityKey: "workUnits", valueField: "WorkUnitCode", labelFields: ["WorkUnitCode", "WorkUnitName"], filterByHolidayCalendarCountry: true }, hint: "Select ALL or one/more work units from the selected holiday calendar country" },
    { name: "IsActive", label: "Active", type: "checkbox" },
  ],
  employeePolicyAssignments: [
    { name: "EmployeeCode", label: "Employee Code", required: true },
    { name: "LegalEntityCode", label: "Legal Entity", source: { entityKey: "legalEntities", valueField: "LegalEntityCode", labelFields: ["LegalEntityCode", "LegalEntityName"] } },
    { name: "LocationCode", label: "Location", source: { entityKey: "locations", valueField: "LocationCode", labelFields: ["LocationCode", "LocationName"] } },
    { name: "DepartmentCode", label: "Department", source: { entityKey: "departments", valueField: "DepartmentCode", labelFields: ["DepartmentCode", "DepartmentName"] } },
    { name: "WorkUnitCode", label: "Work Unit", source: { entityKey: "workUnits", valueField: "WorkUnitCode", labelFields: ["WorkUnitCode", "WorkUnitName"] } },
    { name: "LeavePolicyId", label: "Leave Policy", source: { entityKey: "leavePolicies", valueField: "LeavePolicyId", labelFields: ["LeavePolicyId", "PolicyName"] } },
    { name: "AttendancePolicyId", label: "Attendance Policy", source: { entityKey: "attendancePolicies", valueField: "AttendancePolicyId", labelFields: ["AttendancePolicyId", "PolicyName"] } },
    { name: "ShiftPatternId", label: "Shift Pattern", source: { entityKey: "shiftPatterns", valueField: "ShiftPatternId", labelFields: ["ShiftPatternId", "ShiftName"] } },
    { name: "WeeklyOffPatternId", label: "Weekly Off Pattern", source: { entityKey: "weeklyOffPatterns", valueField: "WeeklyOffPatternId", labelFields: ["WeeklyOffPatternId", "PatternName"] } },
    { name: "HolidayCalendarId", label: "Holiday Calendar", source: { entityKey: "holidayCalendars", valueField: "HolidayCalendarId", labelFields: ["HolidayCalendarId", "CalendarName"] } },
    { name: "EffectiveFrom", label: "Effective From", type: "date" },
    { name: "EffectiveTo", label: "Effective To", type: "date" },
    { name: "AssignedBy", label: "Assigned By" },
    { name: "AssignmentReason", label: "Assignment Reason" },
    { name: "IsCurrent", label: "Current", type: "checkbox" },
    { name: "IsActive", label: "Active", type: "checkbox" },
  ],
};

function normalizeKey(key) {
  return String(key || "").toLowerCase().replace(/[^a-z0-9]/g, "");
}

function extractValue(value) {
  if (value === undefined || value === null) return "";
  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") return String(value);
  if (Array.isArray(value)) return value.map(extractValue).filter(Boolean).join("; ");
  if (typeof value === "object") return extractValue(value.Value || value.value || value.Title || value.Email || value.DisplayName || "");
  return String(value);
}

function getField(record, ...candidateNames) {
  if (!record || typeof record !== "object") return "";
  for (const name of candidateNames) {
    if (Object.prototype.hasOwnProperty.call(record, name)) return record[name];
  }
  const lookup = Object.keys(record).reduce((acc, key) => {
    acc[normalizeKey(key)] = record[key];
    return acc;
  }, {});
  for (const name of candidateNames) {
    const normalized = normalizeKey(name);
    if (Object.prototype.hasOwnProperty.call(lookup, normalized)) return lookup[normalized];
  }
  return "";
}

function displayField(record, fieldName) {
  const rawValue = getField(record, fieldName, `${fieldName} `);
  if (fieldName.toLowerCase().includes("date") || fieldName === "EffectiveFrom" || fieldName === "EffectiveTo") {
    const text = extractValue(rawValue);
    if (!text) return "";
    const date = new Date(text);
    if (Number.isNaN(date.getTime())) return text;
    return date.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
  }
  return extractValue(rawValue);
}

function isActive(record) {
  const value = getField(record, "IsActive", "Active");
  return value === true || value === "true" || value === "Yes" || value === 1 || value === "1" || value === "" || value === undefined || value === null;
}

function getRecordTitle(record, config) {
  return displayField(record, config.primaryField) || displayField(record, "Title") || displayField(record, "PolicyName") || displayField(record, "PatternName") || "Record";
}
function getRecordId(record = {}) {
  if (!record || typeof record !== "object") return "";
  return String(
    getField(
      record,
      "id",
      "ID",
      "ItemInternalId",
      "CountryId",
      "countryId",
      "LegalEntityId",
      "legalEntityId",
      "LocationId",
      "locationId",
      "DepartmentId",
      "departmentId",
      "WorkUnitId",
      "workUnitId",
      "EmploymentTypeId",
      "employmentTypeId",
      "ShiftPatternId", "shiftPatternId",
      "WeeklyOffPatternId", "weeklyOffPatternId",
      "HolidayCalendarId", "holidayCalendarId",
      "HolidayCalendarDateId", "holidayCalendarDateId"
    ) ||
      record.id ||
      record.ID ||
      ""
  );
}
function isTruthy(value) {
  return value === true || value === "true" || value === "True" || value === "Yes" || value === "yes" || value === 1 || value === "1";
}

function buildLookup(rows = [], keyField) {
  const lookup = {};
  rows.forEach((row) => {
    const key = displayField(row, keyField);
    if (key) lookup[key] = row;
  });
  return lookup;
}
function inferCountryCodeFromText(...values) {
  const text = values.map(extractValue).filter(Boolean).join(" ").toUpperCase();
  if (!text) return "";
  if (/\bIN\b/.test(text) || /-IN(?![A-Z0-9])/.test(text) || text.includes("-IN-") || text.includes("_IN_")) return "IN";
  if (/\bIND\b/.test(text) || text.includes("-IND-") || text.includes("_IND_")) return "IN";
  if (/\bDE\b/.test(text) || /-DE(?![A-Z0-9])/.test(text) || text.includes("-DE-") || text.includes("_DE_")) return "DE";
  if (/\bGER\b/.test(text) || text.includes("-GER-") || text.includes("GERMANY")) return "DE";
  if (/\bALL\b/.test(text) || /\bGLB\b/.test(text) || text.includes("GLOBAL")) return "ALL";
  return "";
}

function getAssignmentCountryCode(record, masterData) {
  const directCountry = displayField(record, "CountryCode");
  if (directCountry) return directCountry;

  const legalEntityCode = displayField(record, "LegalEntityCode");
  const locationCode = displayField(record, "LocationCode");
  const workUnitCode = displayField(record, "WorkUnitCode");
  const leavePolicyId = displayField(record, "LeavePolicyId");
  const attendancePolicyId = displayField(record, "AttendancePolicyId");
  const shiftPatternId = displayField(record, "ShiftPatternId");
  const weeklyOffPatternId = displayField(record, "WeeklyOffPatternId");
  const holidayCalendarId = displayField(record, "HolidayCalendarId");

  const legalEntity = buildLookup(getRows(masterData, "legalEntities"), "LegalEntityCode")[legalEntityCode];
  const location = buildLookup(getRows(masterData, "locations"), "LocationCode")[locationCode];
  const workUnit = buildLookup(getRows(masterData, "workUnits"), "WorkUnitCode")[workUnitCode];
  const leavePolicy = buildLookup(getRows(masterData, "leavePolicies"), "LeavePolicyId")[leavePolicyId];
  const attendancePolicy = buildLookup(getRows(masterData, "attendancePolicies"), "AttendancePolicyId")[attendancePolicyId];
  const shiftPattern = buildLookup(getRows(masterData, "shiftPatterns"), "ShiftPatternId")[shiftPatternId];
  const weeklyOffPattern = buildLookup(getRows(masterData, "weeklyOffPatterns"), "WeeklyOffPatternId")[weeklyOffPatternId];
  const holidayCalendar = buildLookup(getRows(masterData, "holidayCalendars"), "HolidayCalendarId")[holidayCalendarId];

  return (
    displayField(legalEntity, "CountryCode") ||
    displayField(location, "CountryCode") ||
    displayField(workUnit, "CountryCode") ||
    displayField(leavePolicy, "CountryCode") ||
    displayField(attendancePolicy, "CountryCode") ||
    displayField(shiftPattern, "CountryCode") ||
    displayField(weeklyOffPattern, "CountryCode") ||
    displayField(holidayCalendar, "CountryCode") ||
    inferCountryCodeFromText(
      legalEntityCode,
      locationCode,
      workUnitCode,
      leavePolicyId,
      attendancePolicyId,
      shiftPatternId,
      weeklyOffPatternId,
      holidayCalendarId
    ) ||
    ""
  );
}

function getRecordStatusLabel(record, config) {
  if (config?.key === "employeePolicyAssignments") {
    if (isTruthy(getField(record, "IsCurrent"))) return "Current";
    return isActive(record) ? "Historical" : "Inactive";
  }
  return isActive(record) ? "Active" : "Inactive";
}

function getStatusPillStyle(record, config) {
  const label = getRecordStatusLabel(record, config);
  if (label === "Current" || label === "Active") return styles.activePill;
  if (label === "Historical") return styles.historyPill;
  return styles.inactivePill;
}

function getDerivedCountryCode(record, config, masterData) {
  const directCountry = displayField(record, "CountryCode");
  if (directCountry) return directCountry;

  if (config.key === "employeePolicyAssignments") {
    return getAssignmentCountryCode(record, masterData);
  }

  if (config.key === "departments") {
    const legalEntityCode = displayField(record, "LegalEntityCode");
    const legalEntities = Array.isArray(masterData.legalEntities) ? masterData.legalEntities : [];
    const legalEntityLookup = buildLookup(legalEntities, "LegalEntityCode");
    return displayField(legalEntityLookup[legalEntityCode], "CountryCode");
  }
  if (config.key === "leavePolicyRules") {
    const leavePolicyId = displayField(record, "LeavePolicyId");
    const leavePolicies = Array.isArray(masterData.leavePolicies) ? masterData.leavePolicies : [];
    const leavePolicyLookup = buildLookup(leavePolicies, "LeavePolicyId");
    return displayField(leavePolicyLookup[leavePolicyId], "CountryCode");
  }
  if (config.key === "holidayCalendarDates") {
    const holidayCalendarId = displayField(record, "HolidayCalendarId");
    const holidayCalendars = Array.isArray(masterData.holidayCalendars) ? masterData.holidayCalendars : [];
    const holidayCalendarLookup = buildLookup(holidayCalendars, "HolidayCalendarId");
    return displayField(holidayCalendarLookup[holidayCalendarId], "CountryCode");
  }
  return "";
}

function isEditorFieldEnabled(field, record) {
  if (!field.dependsOn) return true;
  return record?.[field.dependsOn.field] === field.dependsOn.equals;
}
function InfoHint({ text, label, required = false }) {
  const descriptionId = useId();
  const [open, setOpen] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ left: 16, top: 16, width: 260 });
  const showTooltip = (event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const viewportWidth = Math.max(document.documentElement.clientWidth || 0, window.innerWidth || 0);
    const width = Math.max(160, Math.min(260, viewportWidth - 32));
    const centeredLeft = rect.left + (rect.width / 2) - (width / 2);
    const left = Math.max(16, Math.min(centeredLeft, viewportWidth - width - 16));
    setTooltipPosition({ left, top: rect.bottom + 8, width });
    setOpen(true);
  };
  if (!text) return null;
  return (
    <span style={styles.infoHintWrap}>
      <button type="button" style={{ ...styles.infoHintButton, color: required ? "#b42318" : "#15803d" }} aria-label={`${required ? "Required" : "Optional"} field information about ${label}`} aria-describedby={descriptionId} aria-expanded={open} onMouseEnter={showTooltip} onMouseLeave={() => setOpen(false)} onFocus={showTooltip} onBlur={() => setOpen(false)} onClick={() => setOpen((value) => !value)}><em style={styles.infoHintGlyph}>i</em></button>
      <span id={descriptionId} role="tooltip" style={{ ...styles.infoHintTooltip, ...tooltipPosition, ...(open ? styles.infoHintTooltipOpen : {}) }}>{text}</span>
    </span>
  );
}
function normalizeEditorValue(field, value) {
  if (field.type === "checkbox") return isTruthy(value);
  if (field.type === "number") return value === "" || value === undefined || value === null ? "" : Number(value);
  if (field.type === "date") return toDateInputValue(value);
  if (field.type === "multiselect") return Array.isArray(value) ? value.join(";") : (value ?? "");
  return value ?? "";
}

function toDateInputValue(value) {
  const text = extractValue(value);
  if (!text) return "";
  const date = new Date(text);
  if (Number.isNaN(date.getTime())) return text;
  return date.toISOString().slice(0, 10);
}

function toCodeSegment(value, fallback = "X") {
  const text = extractValue(value).trim().toUpperCase().replace(/[^A-Z0-9]+/g, "-").replace(/^-+|-+$/g, "");
  return text || fallback;
}

function shortCode(value, fallback = "X", length = 6) {
  return toCodeSegment(value, fallback).slice(0, length);
}

function padSequence(number) {
  return String(number || 1).padStart(3, "0");
}

function getRows(masterData, entityKey) {
  return Array.isArray(masterData?.[entityKey]) ? masterData[entityKey] : [];
}

function getActiveRows(masterData, entityKey) {
  return getRows(masterData, entityKey).filter(isActive);
}

function getSourceOptions(field, masterData, editorRecord) {
  if (Array.isArray(field.options)) {
    return field.options.map((option) => ({ value: option, label: option }));
  }

  if (field.dynamicSource === "scopeValue") {
    const scopeType = editorRecord.ScopeType;
    const scopeMap = {
      Country: { entityKey: "countries", valueField: "CountryCode", labelFields: ["CountryCode", "CountryName"] },
      LegalEntity: { entityKey: "legalEntities", valueField: "LegalEntityCode", labelFields: ["LegalEntityCode", "LegalEntityName"] },
      Location: { entityKey: "locations", valueField: "LocationCode", labelFields: ["LocationCode", "LocationName"] },
      Department: { entityKey: "departments", valueField: "DepartmentCode", labelFields: ["DepartmentCode", "DepartmentName"] },
      WorkUnit: { entityKey: "workUnits", valueField: "WorkUnitCode", labelFields: ["WorkUnitCode", "WorkUnitName"] },
    };
    const source = scopeMap[scopeType];
    if (!source) return [];
    return buildSourceOptions(source, masterData, editorRecord);
  }

  if (field.source) {
    return buildSourceOptions(field.source, masterData, editorRecord);
  }

  return [];
}

function buildSourceOptions(source, masterData, editorRecord) {
  const rows = getActiveRows(masterData, source.entityKey);
  const filters = Array.isArray(source.filters) ? source.filters : [];

  return rows
    .filter((row) => {
      if (source.filterByHolidayCalendarCountry) {
        const selectedCalendarId = extractValue(editorRecord?.HolidayCalendarId);
        if (selectedCalendarId) {
          const calendar = getRows(masterData, "holidayCalendars").find((item) => extractValue(getField(item, "HolidayCalendarId")) === selectedCalendarId);
          const calendarCountry = extractValue(getField(calendar, "CountryCode"));
          const rowCountry = extractValue(getField(row, "CountryCode"));
          if (calendarCountry && rowCountry && rowCountry !== "ALL" && rowCountry !== calendarCountry) return false;
        }
      }

      return filters.every((filter) => {
        const selectedValue = extractValue(editorRecord?.[filter.field]);
        if (!selectedValue) return true;
        const rowValue = extractValue(getField(row, filter.targetField));
        if (!rowValue || rowValue === "ALL") return true;
        return rowValue === selectedValue;
      });
    })
    .map((row) => {
      const value = extractValue(getField(row, source.valueField));
      const labelParts = (source.labelFields || [source.valueField])
        .map((fieldName) => extractValue(getField(row, fieldName)))
        .filter(Boolean);
      return { value, label: labelParts.join(" - ") || value };
    })
    .filter((option, index, array) => option.value && array.findIndex((item) => item.value === option.value) === index)
    .sort((a, b) => a.label.localeCompare(b.label));
}

function findGovernedCountry(masterData, value) {
  const needle = extractValue(value).trim().toLowerCase();
  if (!needle) return null;
  return getRows(masterData, "countries").find((row) =>
    ["CountryCode", "countryCode", "CountryName", "countryName"].some((key) => extractValue(getField(row, key)).trim().toLowerCase() === needle)
  ) || null;
}
function applyCountrySmartDefaults(record, masterData) {
  const row = findGovernedCountry(masterData, record.CountryCode || record.CountryName);
  if (!row) return record;
  const pick = (...keys) => extractValue(getField(row, ...keys));
  return { ...record, CountryCode: pick("CountryCode","countryCode") || record.CountryCode,
    CountryName: pick("CountryName","countryName") || record.CountryName,
    ISOAlpha3Code: pick("ISOAlpha3Code","isoAlpha3Code"), ISONumericCode: pick("ISONumericCode","isoNumericCode"),
    ShortName: pick("ShortName","shortName"), RegionGroup: pick("RegionGroupCode","regionGroupCode"),
    DefaultCurrency: pick("DefaultCurrencyCode","defaultCurrencyCode"), DefaultTimeZone: pick("DefaultTimeZoneCode","defaultTimeZoneCode"),
    DefaultLanguageCode: pick("DefaultLanguageCode","defaultLanguageCode") || "EN", PhoneCountryCode: pick("PhoneCountryCode","phoneCountryCode"),
    DateFormatCode: pick("DateFormatCode","dateFormatCode"), NumberFormatCode: pick("NumberFormatCode","numberFormatCode"),
    WeekStartDay: pick("WeekStartDay","weekStartDay"), DisplayOrder: pick("DisplayOrder","displayOrder") || record.DisplayOrder };
}
function applyLocationSmartDefaults(record, masterData, changedField) {
  let next={...record}; const country=findGovernedCountry(masterData,next.CountryCode);
  if (country) {
    if (!next.TimeZone || changedField==="CountryCode") next.TimeZone=extractValue(getField(country,"DefaultTimeZoneCode","defaultTimeZoneCode"));
    if (!next.DefaultLanguageCode || changedField==="CountryCode") next.DefaultLanguageCode=extractValue(getField(country,"DefaultLanguageCode","defaultLanguageCode")) || "EN";
  }
  if (["City","PostalCode"].includes(changedField) && next.City && next.PostalCode) {
    const matches=getActiveRows(masterData,"locations").filter((r) => extractValue(getField(r,"CountryCode"))===extractValue(next.CountryCode) && extractValue(getField(r,"City")).toLowerCase()===extractValue(next.City).toLowerCase() && extractValue(getField(r,"PostalCode"))===extractValue(next.PostalCode));
    if (matches.length===1) { next.Latitude=getField(matches[0],"Latitude") ?? ""; next.Longitude=getField(matches[0],"Longitude") ?? ""; next.TimeZone=extractValue(getField(matches[0],"TimeZone")) || next.TimeZone; next.CoordinateProvenance="Imported"; }
    else { next.Latitude=""; next.Longitude=""; next.CoordinateProvenance="Manual"; }
  }
  if (["AddressLine1","AddressLine2","RegionState"].includes(changedField)) next.CoordinateProvenance="Manual";
  return next;
}
function getSequenceForPrefix(records, primaryField, prefix) {
  const matching = (records || []).filter((record) => extractValue(getField(record, primaryField)).startsWith(prefix));
  return matching.length + 1;
}

function buildSuggestedCode(entityKey, record, records, primaryField) {
  const country = shortCode(record.CountryCode, "ALL", 3);
  const location = shortCode(record.LocationCode, shortCode(record.City, "LOC", 3), 6);
  const employment = shortCode(Array.isArray(record.EmploymentType) ? "MULTI" : (record.EmploymentType || record.EmploymentTypeName), "GEN", 5);
  const year = shortCode(record.Year, String(new Date().getFullYear()), 4);

  let prefix = "";

  switch (entityKey) {
    case "legalEntities":
      prefix = `LE-${country}`;
      break;
    case "locations":
      prefix = `LOC-${country}-${shortCode(record.City, "CITY", 4)}`;
      break;
    case "departments":
      prefix = `DEPT-${shortCode(record.DepartmentName, "DEPT", 8)}`;
      break;
    case "workUnits":
      prefix = `WU-${country}-${location}-${shortCode(record.WorkUnitType, "WU", 4)}`;
      break;
    case "employmentTypes":
      return `EMP-${country}-${shortCode(record.EmploymentTypeName, "TYPE", 8)}`;
    case "attendancePolicies":
      prefix = `ATTPOL-${country}-${employment}`;
      break;
    case "leavePolicies":
      prefix = `LVPOL-${country}-${employment}`;
      break;
    case "shiftPatterns":
      prefix = `SHIFT-${country}-${location}-${shortCode(record.ShiftType, "SHIFT", 5)}`;
      break;
    case "weeklyOffPatterns":
      prefix = `WO-${country}-${location}-${shortCode(record.WeeklyOffType, "WO", 4)}`;
      break;
    case "holidayCalendars":
      prefix = `HOL-${country}-${year}`;
      break;
    default:
      return extractValue(record?.[primaryField]);
  }

  return `${prefix}-${padSequence(getSequenceForPrefix(records, primaryField, prefix))}`;
}

function applyGeneratedPrimaryCode(config, record, records, mode) {
  if (mode === "edit") return record;
  const fields = EDITOR_FIELDS[config.key] || [];
  const primaryField = config.primaryField;
  const primaryMeta = fields.find((field) => field.name === primaryField);
  if (!primaryMeta?.generated) return record;

  const generated = buildSuggestedCode(config.key, record, records, primaryField);
  return generated ? { ...record, [primaryField]: generated } : record;
}

function isPrimaryCodeDuplicate(records, config, candidate, selectedRecord, mode) {
  if (!candidate || !config?.primaryField) return false;
  const currentId = getRecordId(selectedRecord);
  return (records || []).some((record) => {
    if (mode === "edit" && currentId && getRecordId(record) === currentId) return false;
    return extractValue(getField(record, config.primaryField)) === candidate;
  });
}



const AUDIT_LOG_DISPLAY_FIELDS = [
  "ChangedOn",
  "ActionType",
  "EntityName",
  "RecordId",
  "EmployeeCode",
  "ChangedBy",
  "ChangeReason",
  "SourceModule",
];

function getAuditLogRows(masterData) {
  const candidates = [
    masterData?.policyAuditLog,
    masterData?.hrPolicyAuditLog,
    masterData?.HRPolicyAuditLog,
    masterData?.auditLog,
    masterData?.auditLogs,
  ];

  const rows = candidates.find((candidate) => Array.isArray(candidate)) || [];
  return [...rows].sort((a, b) => {
    const aDate = new Date(extractValue(getField(a, "ChangedOn", "Changed On", "Modified", "Created"))).getTime();
    const bDate = new Date(extractValue(getField(b, "ChangedOn", "Changed On", "Modified", "Created"))).getTime();
    return (Number.isNaN(bDate) ? 0 : bDate) - (Number.isNaN(aDate) ? 0 : aDate);
  });
}

function displayAuditValue(record, fieldName) {
  const value = getField(record, fieldName, `${fieldName} `);
  if (fieldName === "ChangedOn") {
    const text = extractValue(value);
    if (!text) return "";
    const date = new Date(text);
    if (Number.isNaN(date.getTime())) return text;
    return date.toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  }
  return extractValue(value);
}

function isGovernanceReadOnlyEntity(config) {
  return config?.key === "employeePolicyAssignments" || config?.key === "leaveBalances";
}
// Batch 3 write APIs and function contracts are active for these policy entities.
// Employee Policy Assignments and Leave Balances remain governance read-only.
const TRACK_B_READ_ONLY_ENTITY_KEYS = new Set([]);

function isTrackBReadOnlyEntity(config) {
  return TRACK_B_READ_ONLY_ENTITY_KEYS.has(config?.key);
}

function getPermission(config, userRole = "Guest") {
  const role = ROLE_PERMISSIONS[userRole] || ROLE_PERMISSIONS.HRAdmin;
  const canManageGroup = role.editableGroups.includes(config.group);
  const isOperationalWorkUnit = config?.group === "operations" && config?.key === "workUnits";

  if (isOperationalWorkUnit || isGovernanceReadOnlyEntity(config) || isTrackBReadOnlyEntity(config)) {
    return { canAdd: false, canEdit: false, canView: true };
  }

  return {
    canAdd: Boolean(role.canAdd && canManageGroup),
    canEdit: Boolean(role.canEdit && canManageGroup),
    canView: true,
  };
}

function getRowsForConfig(masterData, config) {
  const rows = Array.isArray(masterData[config.key]) ? masterData[config.key] : [];
  return rows;
}

function addUniqueWarning(warnings, seenWarnings, warning) {
  if (!warning || seenWarnings.has(warning)) return;
  seenWarnings.add(warning);
  warnings.push(warning);
}

function getDataQualityWarnings(masterData) {
  const warnings = [];
  const seenWarnings = new Set();
  const checkedEntityKeys = new Set();
  const duplicateAllowedEntityKeys = new Set(["leavePolicyRules"]);

  ENTITY_CONFIG.forEach((config) => {
    if (checkedEntityKeys.has(config.key)) return;
    checkedEntityKeys.add(config.key);

    const rows = getRowsForConfig(masterData, config);
    if (!config.primaryField || rows.length === 0) return;

    const seen = new Set();
    const duplicates = new Set();
    rows.forEach((row) => {
      const key = displayField(row, config.primaryField);
      if (!key && config.key !== "holidayCalendarDates") {
        addUniqueWarning(warnings, seenWarnings, `${config.label}: missing ${config.primaryField}.`);
        return;
      }
      if (!duplicateAllowedEntityKeys.has(config.key)) {
        if (seen.has(key)) duplicates.add(key);
        seen.add(key);
      }
    });
    duplicates.forEach((key) => addUniqueWarning(warnings, seenWarnings, `${config.label}: duplicate ${config.primaryField} found - ${key}.`));
  });

  const legalEntities = buildLookup(getRows(masterData, "legalEntities"), "LegalEntityCode");
  const locations = buildLookup(getRows(masterData, "locations"), "LocationCode");
  const departments = buildLookup(getRows(masterData, "departments"), "DepartmentCode");
  const workUnits = buildLookup(getRows(masterData, "workUnits"), "WorkUnitCode");
  const leavePolicies = buildLookup(getRows(masterData, "leavePolicies"), "LeavePolicyId");
  const attendancePolicies = buildLookup(getRows(masterData, "attendancePolicies"), "AttendancePolicyId");
  const shiftPatterns = buildLookup(getRows(masterData, "shiftPatterns"), "ShiftPatternId");
  const weeklyOffPatterns = buildLookup(getRows(masterData, "weeklyOffPatterns"), "WeeklyOffPatternId");
  const holidayCalendars = buildLookup(getRows(masterData, "holidayCalendars"), "HolidayCalendarId");

  getRows(masterData, "departments").forEach((row) => {
    const departmentCode = displayField(row, "DepartmentCode");
    const legalEntityCode = displayField(row, "LegalEntityCode");
    if (legalEntityCode && !legalEntities[legalEntityCode]) addUniqueWarning(warnings, seenWarnings, `Departments: ${departmentCode || "record"} references missing LegalEntityCode ${legalEntityCode}.`);
  });

  getRows(masterData, "workUnits").forEach((row) => {
    const workUnitCode = displayField(row, "WorkUnitCode");
    const locationCode = displayField(row, "LocationCode");
    const departmentCode = displayField(row, "DepartmentCode");
    if (locationCode && !locations[locationCode]) addUniqueWarning(warnings, seenWarnings, `Work Units: ${workUnitCode || "record"} references missing LocationCode ${locationCode}.`);
    if (departmentCode && !departments[departmentCode]) addUniqueWarning(warnings, seenWarnings, `Work Units: ${workUnitCode || "record"} references missing DepartmentCode ${departmentCode}.`);
  });

  const currentAssignmentsByEmployee = {};
  getRows(masterData, "employeePolicyAssignments").forEach((row) => {
    const employeeCode = displayField(row, "EmployeeCode");
    if (!employeeCode) addUniqueWarning(warnings, seenWarnings, "Employee Policy Assignments: assignment found without EmployeeCode.");

    const leavePolicyId = displayField(row, "LeavePolicyId");
    const attendancePolicyId = displayField(row, "AttendancePolicyId");
    const shiftPatternId = displayField(row, "ShiftPatternId");
    const weeklyOffPatternId = displayField(row, "WeeklyOffPatternId");
    const holidayCalendarId = displayField(row, "HolidayCalendarId");
    const legalEntityCode = displayField(row, "LegalEntityCode");
    const locationCode = displayField(row, "LocationCode");
    const departmentCode = displayField(row, "DepartmentCode");
    const workUnitCode = displayField(row, "WorkUnitCode");

    if (leavePolicyId && !leavePolicies[leavePolicyId]) addUniqueWarning(warnings, seenWarnings, `Employee Policy Assignments: ${employeeCode || "record"} references missing LeavePolicyId ${leavePolicyId}.`);
    if (attendancePolicyId && !attendancePolicies[attendancePolicyId]) addUniqueWarning(warnings, seenWarnings, `Employee Policy Assignments: ${employeeCode || "record"} references missing AttendancePolicyId ${attendancePolicyId}.`);
    if (shiftPatternId && !shiftPatterns[shiftPatternId]) addUniqueWarning(warnings, seenWarnings, `Employee Policy Assignments: ${employeeCode || "record"} references missing ShiftPatternId ${shiftPatternId}.`);
    if (weeklyOffPatternId && !weeklyOffPatterns[weeklyOffPatternId]) addUniqueWarning(warnings, seenWarnings, `Employee Policy Assignments: ${employeeCode || "record"} references missing WeeklyOffPatternId ${weeklyOffPatternId}.`);
    if (holidayCalendarId && !holidayCalendars[holidayCalendarId]) addUniqueWarning(warnings, seenWarnings, `Employee Policy Assignments: ${employeeCode || "record"} references missing HolidayCalendarId ${holidayCalendarId}.`);
    if (legalEntityCode && !legalEntities[legalEntityCode]) addUniqueWarning(warnings, seenWarnings, `Employee Policy Assignments: ${employeeCode || "record"} references missing LegalEntityCode ${legalEntityCode}.`);
    if (locationCode && !locations[locationCode]) addUniqueWarning(warnings, seenWarnings, `Employee Policy Assignments: ${employeeCode || "record"} references missing LocationCode ${locationCode}.`);
    if (departmentCode && !departments[departmentCode]) addUniqueWarning(warnings, seenWarnings, `Employee Policy Assignments: ${employeeCode || "record"} references missing DepartmentCode ${departmentCode}.`);
    if (workUnitCode && !workUnits[workUnitCode]) addUniqueWarning(warnings, seenWarnings, `Employee Policy Assignments: ${employeeCode || "record"} references missing WorkUnitCode ${workUnitCode}.`);

    const effectiveFrom = displayField(row, "EffectiveFrom");
    const effectiveTo = displayField(row, "EffectiveTo");
    if (!effectiveFrom) addUniqueWarning(warnings, seenWarnings, `Employee Policy Assignments: ${employeeCode || "record"} has no EffectiveFrom date.`);
    if (effectiveFrom && effectiveTo) {
      const fromDate = new Date(extractValue(getField(row, "EffectiveFrom")));
      const toDate = new Date(extractValue(getField(row, "EffectiveTo")));
      if (!Number.isNaN(fromDate.getTime()) && !Number.isNaN(toDate.getTime()) && fromDate > toDate) addUniqueWarning(warnings, seenWarnings, `Employee Policy Assignments: ${employeeCode || "record"} has EffectiveFrom later than EffectiveTo.`);
    }

    if (isTruthy(getField(row, "IsCurrent"))) currentAssignmentsByEmployee[employeeCode] = (currentAssignmentsByEmployee[employeeCode] || 0) + 1;
  });

  Object.entries(currentAssignmentsByEmployee).forEach(([employeeCode, count]) => {
    if (employeeCode && count > 1) addUniqueWarning(warnings, seenWarnings, `Employee Policy Assignments: ${employeeCode} has ${count} current assignments. EmployeeMaster save flow should keep only one current assignment.`);
  });

  getRows(masterData, "leavePolicies").forEach((row) => {
    const id = displayField(row, "LeavePolicyId");
    if (id.startsWith("ATTPOL-")) addUniqueWarning(warnings, seenWarnings, `Attendance policy ID appears in Leave Policies: ${id}.`);
    const fromText = extractValue(getField(row, "EffectiveFrom"));
    const toText = extractValue(getField(row, "EffectiveTo"));
    if (fromText && toText) {
      const fromDate = new Date(fromText);
      const toDate = new Date(toText);
      if (!Number.isNaN(fromDate.getTime()) && !Number.isNaN(toDate.getTime()) && fromDate > toDate) addUniqueWarning(warnings, seenWarnings, `Invalid effective date range in Leave Policies: ${id}.`);
    }
  });

  return warnings;
}
function HRSettings() {
  const roleInfo = useRole();
  const currentUserRole = roleInfo?.role || "Guest";
  const currentUserId = roleInfo?.email || "";
  const [masterData, setMasterData] = useState({});
  const [activeGroup, setActiveGroup] = useState("organization");
  const [activeKey, setActiveKey] = useState("countries");
  const [searchText, setSearchText] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [countryFilter, setCountryFilter] = useState("All");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [showEditor, setShowEditor] = useState(false);
  const [editorMode, setEditorMode] = useState("add");
  const [editorRecord, setEditorRecord] = useState({});
  const [saving, setSaving] = useState(false);

  const groupConfig = GROUPS.find((group) => group.key === activeGroup) || GROUPS[0];
  const groupEntities = ENTITY_CONFIG.filter((entity) => entity.group === activeGroup);
  const activeConfig = groupEntities.find((item) => item.key === activeKey) || groupEntities[0] || ENTITY_CONFIG[0];
  const records = getRowsForConfig(masterData, activeConfig);
  const permission = getPermission(activeConfig, currentUserRole);
  const dataQualityWarnings = useMemo(() => getDataQualityWarnings(masterData), [masterData]);
  const auditLogRows = useMemo(() => getAuditLogRows(masterData), [masterData]);
  const [auditSearch, setAuditSearch] = useState("");
  const [auditActionFilter, setAuditActionFilter] = useState("");
  const [auditEntityFilter, setAuditEntityFilter] = useState("");
  const auditActionOptions = useMemo(() => [...new Set(auditLogRows.map((r) => displayAuditValue(r, "ActionType")).filter(Boolean))].sort(), [auditLogRows]);
  const auditEntityOptions = useMemo(() => [...new Set(auditLogRows.map((r) => displayAuditValue(r, "EntityName")).filter(Boolean))].sort(), [auditLogRows]);
  const filteredAuditLogRows = useMemo(() => { const q=auditSearch.trim().toLowerCase(); return auditLogRows.filter((r) => { const a=displayAuditValue(r,"ActionType"), e=displayAuditValue(r,"EntityName"); return (!auditActionFilter||a===auditActionFilter)&&(!auditEntityFilter||e===auditEntityFilter)&&(!q||AUDIT_LOG_DISPLAY_FIELDS.some((f)=>String(displayAuditValue(r,f)||"").toLowerCase().includes(q))); }); }, [auditLogRows,auditSearch,auditActionFilter,auditEntityFilter]);

  const countryOptions = useMemo(() => {
    const values = new Set(["All"]);
    ENTITY_CONFIG.forEach((config) => {
      const rows = getRowsForConfig(masterData, config);
      rows.forEach((row) => {
        const countryCode = getDerivedCountryCode(row, config, masterData) || displayField(row, "CountryCode");
        if (countryCode && countryCode !== "ALL") values.add(countryCode);
      });
    });
    return [...values];
  }, [masterData]);

  const summaryCards = useMemo(() => {
    return GROUPS.map((group) => {
      const entities = ENTITY_CONFIG.filter((entity) => entity.group === group.key);
      const count = entities.reduce((sum, entity) => sum + getRowsForConfig(masterData, entity).length, 0);
      const activeCount = entities.reduce((sum, entity) => sum + getRowsForConfig(masterData, entity).filter(isActive).length, 0);
      return { ...group, count, activeCount };
    });
  }, [masterData]);

  const sectionCards = useMemo(() => {
    return groupEntities.map((config) => {
      const rows = getRowsForConfig(masterData, config);
      return { ...config, count: rows.length, activeCount: rows.filter(isActive).length };
    });
  }, [groupEntities, masterData]);

  const filteredRecords = useMemo(() => {
    const q = searchText.trim().toLowerCase();
    return records.filter((record) => {
      const text = activeConfig.displayFields.map((field) => displayField(record, field)).join(" ").toLowerCase();
      const matchesSearch = !q || text.includes(q);
      const matchesStatus =
        statusFilter === "All" ||
        (statusFilter === "Current" ? isTruthy(getField(record, "IsCurrent")) :
          statusFilter === "Historical" ? !isTruthy(getField(record, "IsCurrent")) && isActive(record) :
            statusFilter === "Active" ? isActive(record) : !isActive(record));
      const recordCountry = getDerivedCountryCode(record, activeConfig, masterData);
      const matchesCountry =
        countryFilter === "All" ||
        recordCountry === "ALL" ||
        recordCountry === countryFilter ||
        (activeConfig.key !== "employeePolicyAssignments" && !recordCountry);
      return matchesSearch && matchesStatus && matchesCountry;
    });
  }, [records, activeConfig, masterData, searchText, statusFilter, countryFilter]);

  const loadMasterData = async ({ silent = false } = {}) => {
    try {
      setLoading(true);
      setMessage("");
      const data = await getHRSettingsMasterData();
      const source = data?.body || data || {};
      setMasterData(source);
      if (!silent) setMessage("HR Settings master data loaded successfully.");
    } catch (error) {
      console.error("HR Settings load error:", error);
      setMessage(error.message || "Failed to load HR Settings master data.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMasterData();
  }, []);

  const handleGroupChange = (groupKey) => {
    const firstEntity = ENTITY_CONFIG.find((entity) => entity.group === groupKey);
    setActiveGroup(groupKey);
    setActiveKey(firstEntity?.key || "countries");
    setSelectedRecord(null);
    setShowEditor(false);
    setSearchText("");
    setStatusFilter("All");
  };


  const getEditableFields = () => EDITOR_FIELDS[activeConfig.key] || [];
  const editorAvailable = getEditableFields().length > 0;

  const openAddEditor = () => {
    if (!editorAvailable) {
      setMessage(`${activeConfig.label} editor is not configured yet.`);
      return;
    }

    const now = new Date();
    const localToday = new Date(now.getTime() - now.getTimezoneOffset() * 60000)
      .toISOString().slice(0, 10);
    const displayOrders = records.map((row) => Number(getField(row, "DisplayOrder")))
      .filter((value) => Number.isInteger(value) && value >= 0);
    let initial = {
      IsActive: true,
      EffectiveFrom: localToday,
      DisplayOrder: displayOrders.length ? Math.max(...displayOrders) + 10 : 10,
    };
    getEditableFields().forEach((field) => {
      if (initial[field.name] !== undefined) return;
      if (field.type === "checkbox") {
        initial[field.name] = field.name === "IsActive" || field.name === "IsCurrent";
      } else {
        initial[field.name] = "";
      }
    });

    initial = applyGeneratedPrimaryCode(activeConfig, initial, records, "add");
    setEditorMode("add");
    setEditorRecord(initial);
    setShowEditor(true);
    setSelectedRecord(null);
  };
  const openEditEditor = () => {
    if (!editorAvailable) {
      setMessage(`${activeConfig.label} editor is not configured yet.`);
      return;
    }

    if (!selectedRecord) {
      setMessage("Please click View on a record before editing.");
      return;
    }

    const next = {};
    getEditableFields().forEach((field) => {
      const rawValue = getField(selectedRecord, field.name, `${field.name} `);
      if (field.type === "multiselect") {
        const text = extractValue(rawValue);
        next[field.name] = text ? text.split(";").map((item) => item.trim()).filter(Boolean) : [];
      } else {
        next[field.name] = normalizeEditorValue(field, rawValue);
      }
    });

    const rowVersion = Number(
      getField(
        selectedRecord,
        "RowVersion",
        "rowVersion"
      )
    );

    if (
      !Number.isInteger(rowVersion) ||
      rowVersion < 1
    ) {
      setMessage(
        "The selected Track B record does not contain a valid RowVersion. Refresh and try again."
      );
      return;
    }

    next.expectedRowVersion = rowVersion;
    setEditorMode("edit");
    setEditorRecord(next);
    setShowEditor(true);
  };

  const updateEditorField = (fieldName, value) => {
    setEditorRecord((prev) => {
      const next = { ...prev, [fieldName]: value };

      if (fieldName === "ScopeType") {
        next.ScopeValue = "";
      }
      if (fieldName === "CountryCode") {
        if (activeConfig.key === "locations" || activeConfig.key === "departments") {
          next.LegalEntityCode = "";
          if (activeConfig.key === "departments") next.ParentDepartmentCode = "";
        }
        if (activeConfig.key === "workUnits") {
          next.LegalEntityCode = "";
          next.LocationCode = "";
          next.DepartmentCode = "";
          next.ParentWorkUnitCode = "";
        }
      }
      if (fieldName === "LegalEntityCode") {
        if (activeConfig.key === "departments") {
          next.ParentDepartmentCode = "";
        }
        if (activeConfig.key === "workUnits") {
          next.LocationCode = "";
          next.DepartmentCode = "";
          next.ParentWorkUnitCode = "";
        }
      }
      if (fieldName === "DepartmentCode" && activeConfig.key === "workUnits") {
        next.ParentWorkUnitCode = "";
      }
      if (fieldName === "IsMESEnabled" && value !== true) {
        next.MESWorkCenterReference = "";
      }

      let smart = next;
      if (activeConfig.key === "countries" && ["CountryCode","CountryName"].includes(fieldName)) smart = applyCountrySmartDefaults(smart, masterData);
      if (activeConfig.key === "locations") smart = applyLocationSmartDefaults(smart, masterData, fieldName);
      if (["attendancePolicies","leavePolicies"].includes(activeConfig.key) && fieldName === "CountryCode") { smart.LegalEntityCode=""; smart.LocationCode=""; smart.EmploymentType=[]; }
      if (["attendancePolicies","leavePolicies"].includes(activeConfig.key) && fieldName === "LegalEntityCode") smart.LocationCode="";
      if (["shiftPatterns","weeklyOffPatterns"].includes(activeConfig.key) && fieldName === "CountryCode") { smart.LocationCode=""; smart.WorkUnitCode=""; smart.EmploymentType=""; }
      if (["shiftPatterns","weeklyOffPatterns"].includes(activeConfig.key) && fieldName === "LocationCode") smart.WorkUnitCode="";
      if (activeConfig.key === "holidayCalendars" && fieldName === "CountryCode") { smart.LegalEntityCode=""; smart.LocationCode=""; }
      return applyGeneratedPrimaryCode(activeConfig, smart, records, editorMode);
    });
  };
  const validateEditor = () => {
    const fields = getEditableFields();
    for (const field of fields) {
      if (field.required && !String(editorRecord[field.name] || "").trim()) {
        return `${field.label} is required.`;
      }
    }
    if (editorRecord.EffectiveFrom && editorRecord.EffectiveTo && editorRecord.EffectiveTo < editorRecord.EffectiveFrom) return "Effective To cannot be before Effective From.";
    if (activeConfig.key === "locations") {
      const lat=editorRecord.Latitude, lon=editorRecord.Longitude;
      if ((lat === "") !== (lon === "")) return "Latitude and Longitude must be provided together.";
      if (lat !== "" && (!Number.isFinite(Number(lat)) || Number(lat)<-90 || Number(lat)>90)) return "Latitude must be between -90 and 90.";
      if (lon !== "" && (!Number.isFinite(Number(lon)) || Number(lon)<-180 || Number(lon)>180)) return "Longitude must be between -180 and 180.";
    }
    if (activeConfig.key === "holidayCalendars" && Boolean(editorRecord.CalendarScopeType) !== Boolean(editorRecord.CalendarScopeReference)) return "Calendar Scope Type and Reference must be provided together.";
    if (activeConfig.key === "holidayCalendarDates" && Boolean(editorRecord.StartTime) !== Boolean(editorRecord.EndTime)) return "Start Time and End Time must be provided together.";
    return "";
  };

  const buildEditorPayload = () => {
    const payload = {};
    const fields = getEditableFields();
    fields.forEach((field) => {
      if (field.uiOnly) return;
      const value = editorRecord[field.name];
      if (field.type === "number") {
        payload[field.name] = value === "" || value === null || value === undefined ? null : Number(value);
      } else if (field.type === "multiselect") {
        payload[field.name] = Array.isArray(value) ? value : [];
      } else if (field.type === "checkbox") {
        payload[field.name] = value === true;
      } else {
        payload[field.name] = value === undefined ? "" : value;
      }
    });
    if (editorMode === "edit") {
      payload.expectedRowVersion = Number(editorRecord.expectedRowVersion);
    }
    return payload;
  };

  const handleSaveEditor = async () => {
    const validationMessage = validateEditor();
    if (validationMessage) {
      setMessage(validationMessage);
      return;
    }

    try {
      setSaving(true);
      setMessage("");

      const payload = buildEditorPayload();

      if (editorMode === "add") {
        await addHRSettingRecord(activeConfig.key, payload, {
          changedBy: currentUserRole,
          userId: currentUserId,
          changeReason: "Created from HR Settings Enterprise Console",
        });
      } else {
        const id = getRecordId(selectedRecord);
        if (!id) {
          throw new Error(
            "The selected Track B record does not contain its database ID. Refresh and try again."
          );
        }

        await editHRSettingRecord(activeConfig.key, id, payload, {
          changedBy: currentUserRole,
          userId: currentUserId,
          changeReason: "Updated from HR Settings Enterprise Console",
        });
      }

      setShowEditor(false);
      setSelectedRecord(null);
      await loadMasterData({ silent: true });
      setMessage(editorMode === "add" ? "Record created successfully." : "Record updated successfully.");
    } catch (error) {
      console.error("HR Settings save error:", error);
      setMessage(error.message || "Failed to save HR Settings record.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fm-hr-page-shell" style={styles.page}>
      <div className="fm-hr-hero" style={styles.header}>
        <div>
          <p style={styles.eyebrow}>HR Module / Phase 3F.17F.3</p>
          <h1 style={styles.title}>HR Settings Enterprise Console</h1>
          <p style={styles.subtitle}>Role-aware HR setup console for organization structure, role scope, operational scheduling, HR policies, assignment governance and data quality.</p>
        </div>
        <div style={styles.headerActions}>
          <div style={styles.roleBadge}>Current role: {currentUserRole}</div>
          <button type="button" onClick={loadMasterData} style={styles.secondaryButton} disabled={loading}>{loading ? "Loading..." : "Refresh"}</button>
        </div>
      </div>

      {message && (
        <div style={message.includes("Failed") || message.includes("required") || message.includes("missing") || message.includes("not activated") || message.includes("Please click") || message.includes("does not have") || message.includes("already exists") || message.includes("Effective From") ? styles.errorBox : styles.successBox}>{message}</div>
      )}

      <section style={styles.summaryGrid}>
        {summaryCards.map((item) => (
          <button key={item.key} type="button" onClick={() => handleGroupChange(item.key)} style={item.key === activeGroup ? styles.summaryCardActive : styles.summaryCard}>
            <div style={styles.summaryTop}>
              <strong>{item.label}</strong>
              <span style={styles.badge}>{item.count}</span>
            </div>
            <div style={styles.summaryText}>Active: {item.activeCount} / {item.count}</div>
            <div style={styles.summaryMeta}>{item.description}</div>
          </button>
        ))}
      </section>

      {activeGroup === "quality" && (
        <section style={styles.panel}>
          <div style={styles.panelHeader}>
            <div>
              <h2 style={styles.panelTitle}>Audit & Data Quality</h2>
              <p style={styles.panelSubtitle}>Actionable governance warnings from live HR Settings data. Duplicate system noise is suppressed; Leave Policy Rules can share the same LeavePolicyId by design.</p>
            </div>
          </div>
          {dataQualityWarnings.length === 0 ? (
            <div style={styles.successBox}>No data quality warnings found in the current viewer checks.</div>
          ) : (
            <div style={styles.warningList}>
              {dataQualityWarnings.map((warning) => <div key={warning} style={styles.warningItem}>⚠️ {warning}</div>)}
            </div>
          )}
        </section>
      )}

      {activeGroup !== "quality" && (
        <>
          <section style={styles.sectionGrid}>
            {sectionCards.map((item) => (
              <button key={`${item.group}-${item.key}`} type="button" onClick={() => { setActiveKey(item.key); setSelectedRecord(null); setShowEditor(false); setStatusFilter("All"); }} style={item.key === activeKey ? styles.sectionCardActive : styles.sectionCard}>
                <div style={styles.summaryTop}>
                  <strong>{item.label}</strong>
                  <span style={styles.badge}>{item.count}</span>
                </div>
                <div style={styles.summaryMeta}>{item.listName}</div>
                <div style={styles.summaryText}>Active: {item.activeCount} / {item.count}</div>
              </button>
            ))}
          </section>

          <section style={styles.panel}>
            <div style={styles.panelHeader}>
              <div>
                <h2 style={styles.panelTitle}>{activeConfig.label}</h2>
                <p style={styles.panelSubtitle}>{groupConfig.label} · {activeConfig.accessPolicy}</p>
                <p style={styles.smallText}>List: <strong>{activeConfig.listName}</strong> · Owner: <strong>{activeConfig.ownerRole}</strong></p>
                {isGovernanceReadOnlyEntity(activeConfig) && (
                  <div style={styles.infoBox}>
                    EmployeeMaster is the source of truth for creating and changing employee policy assignments. HRSettings shows assignment records for governance, audit and data-quality review only. Use EmployeeMaster page to create a new current assignment or change policies for an employee.
                  </div>
                )}
                {currentUserRole === "Manager" && activeConfig.key === "workUnits" && (
                  <div style={styles.infoBox}>
                    Work Units are governed organizational masters. Managers can view Work Units but cannot create or modify them. Operational Scheduling assigns approved patterns to existing Work Units.
                  </div>
                )}
                {isTrackBReadOnlyEntity(activeConfig) && (
                  <div style={styles.infoBox}>
                    This collection is supplied by an accepted Track B read-only API. Add and Edit actions remain unavailable until a separately controlled write API is approved and activated.
                  </div>
                )}
              </div>
              <div style={styles.actionRow}>
                {permission.canAdd && !isGovernanceReadOnlyEntity(activeConfig) && !isTrackBReadOnlyEntity(activeConfig) && (
                  <button type="button" style={styles.primaryButton} onClick={openAddEditor} disabled={saving || !editorAvailable} title={editorAvailable ? "Add record" : "Editor not activated for this section yet"}>Add</button>
                )}
                {permission.canEdit && !isGovernanceReadOnlyEntity(activeConfig) && !isTrackBReadOnlyEntity(activeConfig) && (
                  <button type="button" style={styles.secondaryButton} onClick={openEditEditor} disabled={saving || !selectedRecord || !editorAvailable} title={selectedRecord ? "Edit selected record" : "Click View on a record first"}>Edit Selected</button>
                )}
              </div>
            </div>

            <div style={styles.filterGrid}>
              <input style={styles.input} value={searchText} onChange={(event) => setSearchText(event.target.value)} placeholder="Search current section" />
              <select style={styles.select} value={countryFilter} onChange={(event) => setCountryFilter(event.target.value)}>
                {countryOptions.map((country) => <option key={country}>{country}</option>)}
              </select>
              <select style={styles.select} value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
                <option>All</option>
                {activeConfig.key === "employeePolicyAssignments" ? (
                  <>
                    <option>Current</option>
                    <option>Historical</option>
                    <option>Inactive</option>
                  </>
                ) : (
                  <>
                    <option>Active</option>
                    <option>Inactive</option>
                  </>
                )}
              </select>
            </div>

            <div style={styles.tableWrapper}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Record</th>
                    {activeConfig.displayFields.map((field) => <th key={field} style={styles.th}>{field}</th>)}
                    <th style={styles.th}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRecords.length === 0 && (
                    <tr>
                      <td style={styles.emptyCell} colSpan={activeConfig.displayFields.length + 2}>{loading ? "Loading records..." : activeConfig.emptyMessage || "No records found."}</td>
                    </tr>
                  )}
                  {filteredRecords.map((record, index) => (
                    <tr key={`${activeConfig.group}-${activeConfig.key}-${getRecordTitle(record, activeConfig)}-${index}`}>
                      <td style={styles.td}><strong>{getRecordTitle(record, activeConfig)}</strong><div style={getStatusPillStyle(record, activeConfig)}>{getRecordStatusLabel(record, activeConfig)}</div></td>
                      {activeConfig.displayFields.map((field) => (
                        <td key={field} style={styles.td}>
                          {activeConfig.key === "employeePolicyAssignments" && field === "CountryCode"
                            ? getAssignmentCountryCode(record, masterData)
                            : displayField(record, field)}
                        </td>
                      ))}
                      <td style={styles.td}><button type="button" style={styles.linkButton} onClick={() => { setSelectedRecord(record); setShowEditor(false); }}>{selectedRecord && getRecordId(selectedRecord) === getRecordId(record) ? "Viewing" : "View"}</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        </>
      )}


      {showEditor && (
        <section style={styles.editorPanel}>
          <div style={styles.panelHeader}>
            <div>
              <p style={styles.eyebrow}>Phase 3F.17F.3 Add/Edit Editor</p>
              <h2 style={styles.panelTitle}>{editorMode === "add" ? "Add" : "Edit"} {activeConfig.label}</h2>
              <p style={styles.panelSubtitle}>Add/Edit flow · Dependent fields use dropdowns and IDs are generated where applicable.</p>
            </div>
            <button type="button" style={styles.secondaryButton} onClick={() => setShowEditor(false)} disabled={saving}>Close</button>
          </div>

          <div style={styles.editorGrid}>
            {getEditableFields().map((field) => {
              const fieldOptions = getSourceOptions(field, masterData, editorRecord);
              const isGeneratedField = Boolean(field.generated || field.derived);
              const isLockedPrimaryOnEdit = editorMode === "edit" && field.name === activeConfig.primaryField;
              const isSelectField = fieldOptions.length > 0 || field.source || field.dynamicSource;

              return (
                <label key={field.name} style={styles.editorField}>
                  <span style={styles.fieldLabelRow}>
                    <span style={styles.detailKey}>{field.label}{field.required ? " *" : ""}</span>
                    {field.hint && <InfoHint text={field.hint} label={field.label} required={Boolean(field.required)} />}
                  </span>
                  {field.emptyHint && isSelectField && fieldOptions.length === 0 && <span style={styles.emptyFieldHint} role="status">{field.emptyHint}</span>}

                  {field.type === "checkbox" ? (
                    <span style={styles.checkboxRow}>
                      <input
                        type="checkbox"
                        checked={isTruthy(editorRecord[field.name])}
                        onChange={(event) => updateEditorField(field.name, event.target.checked)}
                      />
                      <span>{isTruthy(editorRecord[field.name]) ? "Yes" : "No"}</span>
                    </span>
                  ) : field.type === "multiselect" ? (
                    <select
                      style={{ ...styles.select, minHeight: "96px" }}
                      multiple
                      value={Array.isArray(editorRecord[field.name]) ? editorRecord[field.name] : String(editorRecord[field.name] || "").split(";").filter(Boolean)}
                      onChange={(event) => {
                        const values = Array.from(event.target.selectedOptions).map((option) => option.value);
                        updateEditorField(field.name, values);
                      }}
                      disabled={isLockedPrimaryOnEdit || !isEditorFieldEnabled(field, editorRecord)}
                    >
                      {fieldOptions.map((option) => (
                        <option key={`${field.name}-${option.value}`} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                  ) : isSelectField ? (
                    <select
                      style={styles.select}
                      value={editorRecord[field.name] ?? ""}
                      onChange={(event) => updateEditorField(field.name, event.target.value)}
                      disabled={isLockedPrimaryOnEdit || !isEditorFieldEnabled(field, editorRecord)}
                    >
                      <option value="">Select {field.label}</option>
                      {fieldOptions.map((option) => (
                        <option key={`${field.name}-${option.value}`} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                  ) : (
                    <input
                      style={isGeneratedField || isLockedPrimaryOnEdit ? styles.readOnlyInput : styles.input}
                      type={field.type || "text"}
                      value={editorRecord[field.name] ?? ""}
                      onChange={(event) => updateEditorField(field.name, event.target.value)}
                      placeholder={field.label}
                      readOnly={isGeneratedField || isLockedPrimaryOnEdit || !isEditorFieldEnabled(field, editorRecord)}
                    />
                  )}
                </label>
              );
            })}
          </div>

          <div style={{ ...styles.actionRow, marginTop: "14px" }}>
            <button type="button" style={styles.primaryButton} onClick={handleSaveEditor} disabled={saving}>{saving ? "Saving..." : "Save"}</button>
            <button type="button" style={styles.secondaryButton} onClick={() => setShowEditor(false)} disabled={saving}>Cancel</button>
          </div>
        </section>
      )}

      {selectedRecord && (
        <section style={styles.panel}>
          <div style={styles.panelHeader}>
            <div>
              <h2 style={styles.panelTitle}>Record Detail</h2>
              <p style={styles.panelSubtitle}>{getRecordTitle(selectedRecord, activeConfig)}</p>
            </div>
            <button type="button" style={styles.secondaryButton} onClick={() => setSelectedRecord(null)}>Close detail</button>
          </div>
          <div style={styles.detailGrid}>
            {Object.keys(selectedRecord).filter((key) => key !== "@odata.etag").map((key) => (
              <div key={key} style={styles.detailItem}>
                <div style={styles.detailKey}>{key || "Unnamed field"}</div>
                <div style={styles.detailValue}>{extractValue(selectedRecord[key])}</div>
              </div>
            ))}
          </div>
        </section>
      )}

      <section style={styles.panel}>
        <div style={styles.panelHeader}>
          <div>
            <h2 style={styles.panelTitle}>HR Policy Audit Log</h2>
            <p style={styles.panelSubtitle}>Sanitized HR Settings governance audit records from the Track B API.</p>
          </div>
          <span style={styles.badge}>
            {filteredAuditLogRows.length} of {auditLogRows.length}
          </span>
        </div>
        <div style={styles.filterGrid}>
          <input
            type="search"
            aria-label="Search HR Policy Audit Log"
            placeholder="Search audit records"
            value={auditSearch}
            onChange={(event) => setAuditSearch(event.target.value)}
            style={styles.input}
          />
          <select
            aria-label="Filter audit records by action"
            value={auditActionFilter}
            onChange={(event) => setAuditActionFilter(event.target.value)}
            style={styles.select}
          >
            <option value="">All actions</option>
            {auditActionOptions.map((option) => (
              <option key={option} value={option}>{option}</option>
            ))}
          </select>
          <select
            aria-label="Filter audit records by entity"
            value={auditEntityFilter}
            onChange={(event) => setAuditEntityFilter(event.target.value)}
            style={styles.select}
          >
            <option value="">All entities</option>
            {auditEntityOptions.map((option) => (
              <option key={option} value={option}>{option}</option>
            ))}
          </select>
          <button
            type="button"
            style={styles.secondaryButton}
            onClick={() => {
              setAuditSearch("");
              setAuditActionFilter("");
              setAuditEntityFilter("");
            }}
          >
            Clear filters
          </button>
        </div>
        <div style={styles.tableWrapper}>
          <table style={styles.table}>
            <thead>
              <tr>
                {AUDIT_LOG_DISPLAY_FIELDS.map((field) => (
                  <th key={`audit-${field}`} style={styles.th}>{field}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredAuditLogRows.length === 0 && (
                <tr>
                  <td style={styles.emptyCell} colSpan={AUDIT_LOG_DISPLAY_FIELDS.length}>No audit records match the current search and filters.</td>
                </tr>
              )}
              {filteredAuditLogRows.slice(0, 100).map((record, index) => (
                <tr key={`audit-log-${displayAuditValue(record, "ChangedOn")}-${displayAuditValue(record, "RecordId")}-${index}`}>
                  {AUDIT_LOG_DISPLAY_FIELDS.map((field) => (
                    <td key={`audit-${field}-${index}`} style={styles.td}>{displayAuditValue(record, field)}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section style={styles.panel}>
        <h2 style={styles.panelTitle}>Enterprise build direction</h2>
        <p style={styles.subtitle}>This console is intentionally role-aware and metadata-driven. The frontend can stay stable if the backend moves from SharePoint to Dataverse, SQL, or a dedicated API; only the service layer and entity metadata need to evolve.</p>
      </section>
    </div>
  );
}

const styles = {
  page: { fontFamily: "Segoe UI, Calibri, Arial, sans-serif", padding: "18px", background: "var(--fm-content)", minHeight: "100vh", color: "var(--fm-text)", boxSizing: "border-box", overflowX: "hidden" },
  header: { display: "flex", justifyContent: "space-between", gap: "18px", flexWrap: "wrap", marginBottom: "16px", alignItems: "flex-start", background: "var(--fm-hero-bg)", color: "#ffffff", borderRadius: "20px", padding: "22px 24px", boxShadow: "0 10px 28px rgba(0,80,150,0.16)" },
  eyebrow: { margin: 0, color: "rgba(255,255,255,0.85)", fontWeight: 800, fontSize: "11px", letterSpacing: "0.09em", textTransform: "uppercase" },
  title: { margin: "5px 0", fontSize: "29px", color: "#ffffff", lineHeight: 1.2 },
  subtitle: { margin: 0, color: "rgba(255,255,255,0.92)", maxWidth: "950px", lineHeight: 1.5 },
  headerActions: { display: "flex", gap: "10px", flexWrap: "wrap", alignItems: "center" },
  roleBadge: { background: "rgba(255,255,255,0.18)", color: "#ffffff", border: "1px solid rgba(255,255,255,0.42)", borderRadius: "999px", padding: "9px 12px", fontWeight: 800 },
  primaryButton: { background: "var(--fm-page-accent)", color: "#ffffff", border: "1px solid #0078d4", borderRadius: "10px", padding: "10px 14px", fontWeight: 800, cursor: "pointer" },
  secondaryButton: { background: "#eef6ff", color: "var(--fm-page-accent)", border: "1px solid #bfdbfe", borderRadius: "10px", padding: "10px 14px", fontWeight: 800, cursor: "pointer" },
  dangerButton: { background: "var(--fm-card)", color: "#b91c1c", border: "1px solid #fecaca", borderRadius: "10px", padding: "10px 14px", fontWeight: 800, cursor: "pointer" },
  successBox: { border: "1px solid #bbf7d0", background: "#f0fdf4", color: "#166534", borderRadius: "12px", padding: "12px", marginBottom: "16px", fontWeight: 700 },
  errorBox: { border: "1px solid #fecaca", background: "#fef2f2", color: "#991b1b", borderRadius: "12px", padding: "12px", marginBottom: "16px", fontWeight: 700 },
  infoBox: { border: "1px solid #bfdbfe", background: "#eff6ff", color: "#1e40af", borderRadius: "12px", padding: "12px", marginTop: "10px", fontWeight: 700, lineHeight: 1.45 },
  summaryGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "11px", marginBottom: "14px", minWidth: 0 },
  summaryCard: { textAlign: "left", border: "1px solid var(--fm-border)", borderRadius: "16px", padding: "14px", background: "var(--fm-card)", cursor: "pointer" },
  summaryCardActive: { textAlign: "left", border: "2px solid #0f766e", borderRadius: "16px", padding: "13px", background: "#ecfdf5", cursor: "pointer" },
  sectionGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "12px", marginBottom: "16px" },
  sectionCard: { textAlign: "left", border: "1px solid var(--fm-border)", borderRadius: "14px", padding: "12px", background: "var(--fm-card)", cursor: "pointer" },
  sectionCardActive: { textAlign: "left", border: "2px solid #2563eb", borderRadius: "14px", padding: "11px", background: "#eff6ff", cursor: "pointer" },
  summaryTop: { display: "flex", justifyContent: "space-between", gap: "8px", alignItems: "center" },
  summaryMeta: { color: "var(--fm-muted)", fontSize: "12px", marginTop: "6px", lineHeight: 1.4 },
  summaryText: { color: "var(--fm-text)", fontSize: "13px", marginTop: "8px", fontWeight: 700 },
  badge: { background: "#e0f2fe", color: "var(--fm-page-accent)", borderRadius: "999px", padding: "4px 8px", fontSize: "12px", fontWeight: 800 },
  panel: { background: "var(--fm-card)", border: "1px solid var(--fm-border)", borderRadius: "18px", padding: "16px", boxShadow: "0 3px 13px rgba(15,23,42,0.052)", marginBottom: "14px", minWidth: 0, overflow: "hidden" },
  editorPanel: { background: "var(--fm-card)", border: "2px solid #0078d4", borderRadius: "18px", padding: "18px", boxShadow: "0 8px 20px rgba(15, 118, 110, 0.12)", marginBottom: "16px" },
  panelHeader: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "14px", flexWrap: "wrap", marginBottom: "14px" },
  panelTitle: { margin: 0, fontSize: "20px" },
  panelSubtitle: { margin: "4px 0 0", color: "var(--fm-muted)", fontSize: "14px" },
  smallText: { margin: "8px 0 0", color: "var(--fm-text)", fontSize: "13px" },
  actionRow: { display: "flex", gap: "10px", flexWrap: "wrap" },
  filterGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(170px,1fr))", gap: "10px", marginBottom: "14px", alignItems: "center", minWidth: 0 },
  input: { border: "1px solid var(--fm-border)", borderRadius: "12px", padding: "10px 12px", width: "100%", boxSizing: "border-box" },
  select: { border: "1px solid var(--fm-border)", borderRadius: "12px", padding: "10px 12px", background: "var(--fm-card)", fontWeight: 700, width: "100%", boxSizing: "border-box" },
  tableWrapper: { overflowX: "auto", maxWidth: "100%", border: "1px solid var(--fm-border)", borderRadius: "14px", WebkitOverflowScrolling: "touch" },
  table: { width: "100%", borderCollapse: "collapse", minWidth: "1200px", fontSize: "12.5px" },
  th: { textAlign: "left", borderBottom: "1px solid var(--fm-border)", padding: "10px", background: "#f8fafc", color: "var(--fm-text)", whiteSpace: "nowrap" },
  td: { borderBottom: "1px solid var(--fm-border)", padding: "10px", color: "var(--fm-text)", whiteSpace: "nowrap", verticalAlign: "top" },
  emptyCell: { padding: "18px", textAlign: "center", color: "var(--fm-muted)" },
  activePill: { display: "inline-block", marginTop: "6px", background: "#dcfce7", color: "#166534", borderRadius: "999px", padding: "3px 8px", fontSize: "11px", fontWeight: 800 },
  inactivePill: { display: "inline-block", marginTop: "6px", background: "#fee2e2", color: "#991b1b", borderRadius: "999px", padding: "3px 8px", fontSize: "11px", fontWeight: 800 },
  historyPill: { display: "inline-block", marginTop: "6px", background: "#e0f2fe", color: "var(--fm-page-accent)", borderRadius: "999px", padding: "3px 8px", fontSize: "11px", fontWeight: 800 },
  linkButton: { background: "var(--fm-page-accent)", color: "#ffffff", border: "none", borderRadius: "8px", padding: "7px 11px", fontWeight: 800, cursor: "pointer" },
  detailGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: "10px" },
  detailItem: { border: "1px solid var(--fm-border)", background: "#f8fafc", borderRadius: "12px", padding: "10px" },
  detailKey: { color: "var(--fm-muted)", fontSize: "12px", fontWeight: 800, marginBottom: "6px" },
  detailValue: { color: "var(--fm-text)", fontSize: "13px", wordBreak: "break-word" },
  warningList: { display: "grid", gap: "10px" },
  warningItem: { border: "1px solid #fde68a", background: "#fffbeb", color: "#92400e", borderRadius: "12px", padding: "10px 12px", fontWeight: 700 },
  editorGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: "12px" },
  editorField: { display: "grid", gap: "6px", border: "1px solid var(--fm-border)", background: "#f8fafc", borderRadius: "12px", padding: "10px" },
  checkboxRow: { display: "flex", gap: "8px", alignItems: "center", fontWeight: 800, color: "var(--fm-text)" },
  fieldLabelRow: { display: "flex", alignItems: "center", gap: "6px", minWidth: 0 },
  infoHintWrap: { position: "relative", display: "inline-flex", alignItems: "center" },
  infoHintButton: { width: "16px", height: "16px", minWidth: "16px", borderRadius: "50%", border: "1px solid currentColor", background: "var(--fm-card)", display: "inline-flex", alignItems: "center", justifyContent: "center", padding: 0, cursor: "help", lineHeight: 1, boxShadow: "0 0 0 1px rgba(255,255,255,0.7)" },
  infoHintGlyph: { fontSize: "10px", fontWeight: 900, fontStyle: "italic", lineHeight: 1, fontFamily: "Georgia, serif" },
  infoHintTooltip: { position: "fixed", zIndex: 10000, right: "auto", bottom: "auto", maxWidth: "calc(100vw - 32px)", minWidth: "160px", padding: "8px 10px", borderRadius: "8px", background: "#1f2937", color: "#ffffff", fontSize: "12px", lineHeight: 1.45, boxShadow: "0 6px 18px rgba(15,23,42,0.25)", opacity: 0, visibility: "hidden", pointerEvents: "none", transform: "translateY(4px)", transition: "opacity 0.15s ease, transform 0.15s ease, visibility 0.15s ease", whiteSpace: "normal", overflowWrap: "anywhere", wordBreak: "normal", boxSizing: "border-box", textAlign: "left" },
  infoHintTooltipOpen: { opacity: 1, visibility: "visible", transform: "translateY(0)" },
  emptyFieldHint: { color: "#92400e", background: "#fffbeb", border: "1px solid #fde68a", borderRadius: "8px", padding: "7px", fontSize: "11px", lineHeight: 1.35 },
  readOnlyInput: { border: "1px solid var(--fm-border)", borderRadius: "12px", padding: "10px 12px", width: "100%", boxSizing: "border-box", background: "#e2e8f0", color: "var(--fm-text)", fontWeight: 800 },
};

export default HRSettings;
